/*
 * Camp Kunu Club loyalty page — dynamic renderer.
 *
 * Reads everything from the in-page `window.loyaltylion` object and populates the
 * static prototype markup in sections/loyalty-lion-section.liquid. Also handles the
 * reward/product redemption flows, the redeem search/sort/filters and nav scrollspy.
 *
 * Contract with the Liquid markup (data-* hooks):
 *   - [data-lion-points="approved"]        hero points balance (SDK/JS fills)
 *   - [data-ll="<name>"]                   a single labelled field (hero badge/worth, tier subtitle…)
 *   - [data-ll-section="<name>"]           a whole section (hidden via .ll-hidden until ready)
 *   - [data-ll-grid="<name>"]              a repeating container
 *   - [data-ll-tpl] (inside a grid)        the prototype item (redeem grid carries two: product/coupon)
 *   - [data-ll-field="<name>"]             a field inside an item/section
 *   - [data-ll-grid="tier-desktop|tier-mobile"] tier-progress tracks (a node/row
 *       prototype is cloned per visible program tier; data-ll-threshold = cents)
 *   - [data-ll-tier-icons]                 ordered tier-icon URLs (pipe-joined),
 *       rendered by Liquid; assigned to tiers by position
 *   - [data-ll-more="<grid>"]              a "show more" pager button for a grid
 *   - [data-ll-redeem-search|sort], [data-ll-filter]    redeem controls
 *
 * File layout: module state -> constants/icons -> utilities -> modal ->
 * data/normalization -> popups -> redemption -> renderers -> bootstrap.
 */
(function () {
  'use strict';

  /* =========================================================================
     MODULE STATE
     ========================================================================= */
  var IMG = 'https://rsvlts.vercel.app/images/';
  var ROOT = null;
  var STATE = { customer: null, program: {}, sym: '$', tierId: null, cartSubtotal: 0, presentment: null };
  // Set by initCardPopups() once the reward-preview overlay exists, so the redeem
  // grid can open the combined product preview (image + size buttons + redeem).
  var _openProductPreview = null;

  // Close the reward-preview overlay (r-popup) if it's open. Used after a
  // successful product redeem so the preview gives way to the success popup.
  function closeRewardPreview() {
    var ov = document.querySelector('.r-popup__overlay.is-open');
    if (ov) { ov.classList.remove('is-open'); document.body.style.overflow = ''; }
  }

  // Safari on a slow/unstable connection can leave a request pending forever —
  // neither the resolve nor the reject handler ever runs, so a button switched to
  // "REDEEMING…" stays stuck on that state indefinitely (Chrome's network stack
  // gives up much sooner, which is why the same flow looks fine there). fetch has
  // no built-in timeout in any browser, so every request that gates a button
  // goes through this wrapper: it always settles, rejecting with isTimeout set so
  // callers can tell "we never heard back" apart from a real error response.
  var REQUEST_TIMEOUT_MS = 20000;
  function fetchWithTimeout(url, opts, ms) {
    opts = opts || {};
    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    if (ctrl) opts.signal = ctrl.signal;
    return new Promise(function (resolve, reject) {
      var settled = false;
      // The timer is deliberately NOT cleared once headers arrive: fetch resolves
      // on headers, but the body read (res.json()/res.text()) is a separate
      // promise that can stall just as easily on a flaky connection. Letting the
      // abort fire regardless makes that read reject instead of hanging; once the
      // body has been consumed the abort is a harmless no-op.
      setTimeout(function () {
        // Abort so the socket is released; Safari 12.1+ supports AbortController,
        // and without it the request is merely orphaned rather than cancelled.
        if (ctrl) { try { ctrl.abort(); } catch (e) {} }
        if (settled) return;
        settled = true;
        var err = new Error('timeout');
        err.isTimeout = true;
        reject(err);
      }, ms || REQUEST_TIMEOUT_MS);
      fetch(url, opts).then(function (res) {
        if (settled) return;
        settled = true; resolve(res);
      }, function (e) {
        if (settled) return; // our own abort — already rejected above
        settled = true; reject(e);
      });
    });
  }

  // We only ever abort a request on timeout, so an AbortError is one too — it
  // just surfaced during the body read rather than the headers.
  function isTimeoutError(e) {
    return !!(e && (e.isTimeout || e.name === 'AbortError'));
  }

  /* =========================================================================
     CONSTANTS & ICONS
     ========================================================================= */
  // Inline SVGs used in popups (share buttons, copy, star, clock).
  var ICON = {
    twitter: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18.244 2H21.5l-7.5 8.57L22.5 22h-6.9l-4.6-6.02L5.7 22H2.44l8.02-9.17L1.5 2h6.9l4.16 5.5L18.244 2Zm-1.21 18h1.83L7.05 3.9H5.1l11.93 16.1Z"/></svg>',
    whatsapp: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.02A9.86 9.86 0 0 0 12.04 2Zm5.8 14.13c-.25.69-1.45 1.32-1.99 1.36-.53.04-.53.42-3.34-.7-2.8-1.12-4.5-4.02-4.64-4.2-.13-.18-1.1-1.46-1.1-2.78 0-1.32.69-1.97.94-2.24.25-.27.54-.34.72-.34.18 0 .36 0 .52.01.17.01.39-.06.61.47.25.6.84 2.07.91 2.22.07.15.12.33.02.51-.09.18-.14.3-.27.46-.14.15-.29.35-.41.47-.14.13-.28.28-.12.55.16.27.71 1.17 1.53 1.9 1.05.94 1.94 1.23 2.21 1.36.27.14.43.12.59-.07.16-.18.68-.79.86-1.06.18-.27.36-.22.61-.13.25.09 1.6.75 1.87.89.27.13.45.2.52.31.07.11.07.65-.18 1.34Z"/></svg>',
    messenger: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.19 5.42 3.14 7.16.16.14.26.34.27.56l.05 1.78c.02.57.6.94 1.12.71l1.99-.88c.17-.07.36-.09.54-.04 1.03.28 2.13.43 3.29.43 5.64 0 10-4.13 10-9.7C22.5 6.13 17.64 2 12 2Zm6 7.46-2.94 4.67c-.47.74-1.47.93-2.18.4l-2.34-1.75a.6.6 0 0 0-.72 0l-3.16 2.4c-.42.32-.97-.18-.69-.63l2.94-4.67c.47-.74 1.47-.93 2.18-.4l2.34 1.75a.6.6 0 0 0 .72 0l3.16-2.4c.42-.32.97.18.69.63Z"/></svg>',
    facebook: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.5-3.91 3.78-3.91 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.44 2.91h-2.34V22c4.78-.76 8.44-4.92 8.44-9.94Z"/></svg>',
    email: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm9 7.5L4.5 7h15L12 12.5Zm0 2.2 8-5.9V18H4V8.8l8 5.9Z"/></svg>',
    copy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>',
    star: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.9 6.26L21.5 9l-5 4.6 1.3 6.9L12 17.3 6.2 20.5l1.3-6.9-5-4.6 6.6-.74L12 2z"/></svg>',
    clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>'
  };

  // Earn-rule icons (custom rsvlts art), the LoyaltyLion action slug per rule kind,
  // social profile URLs, and a small clock svg used in the birthday countdown button.
  var RULE_ICON = {
    purchase: IMG + 'purchase.svg',
    signup: IMG + 'join%20program.svg',
    newsletterSignup: IMG + 'mail.svg',
    birthday: IMG + 'happybirthday.svg',
    referral: IMG + 'friends.svg',
    facebookLike: IMG + 'facebook.svg',
    instagramFollow: IMG + 'instagram.svg',
    twitterFollow: IMG + 'X.svg'
  };
  var RULE_SLUG = {
    purchase: 'purchase', signup: 'join', newsletterSignup: 'email_signup',
    birthday: 'birthday', referral: 'referral', facebookLike: 'facebook_like',
    instagramFollow: 'instagram_follow', twitterFollow: 'twitter_follow'
  };
  var SOCIAL = {
    facebookLike: 'https://www.facebook.com/RSVLTS',
    instagramFollow: 'https://www.instagram.com/rsvlts',
    twitterFollow: 'https://x.com/rsvlts'
  };
  var CLOCK_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg>';


  /* =========================================================================
     UTILITIES
     ========================================================================= */
  function num(n) { return (n || 0).toLocaleString('en-US'); }
  function money(cents, sym) { return (sym || '$') + (Math.round(cents) / 100).toLocaleString('en-US'); }

  // Active presentment currency vs the program's base (USD). Shopify exposes the
  // active code + its rate against base; window.currency carries the symbol.
  // Returns null when browsing in the base currency (rate missing/0/1) — no
  // second figure to show. Guarded so a missing global can never throw.
  function presentment() {
    try {
      var sc = (window.Shopify && window.Shopify.currency) || {};
      var rate = parseFloat(sc.rate);
      if (!rate || rate === 1) return null;
      return { rate: rate, sym: (window.currency && window.currency.symbol) || '' };
    } catch (e) { return null; }
  }
  // Local-currency string for a base-currency *dollar* amount, e.g. 100 -> "£75.69".
  // Whole values drop the cents (".00") so zero reads "£0", not "£0.00".
  function localStr(dollars, p) {
    var v = dollars * p.rate;
    return p.sym + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/\.00$/, '');
  }
  // "$100" or, off the base currency, "$100 (£75.69)". cents in base currency.
  function moneyDual(cents, sym) {
    var base = money(cents, sym);
    var p = STATE.presentment;
    return p ? base + ' (' + localStr(Math.round(cents) / 100, p) + ')' : base;
  }
  function fmtDate(iso) {
    var d = new Date(iso);
    return isNaN(d) ? '' : d.toLocaleDateString('en-US');
  }
  // Expiry timestamps are stored end-of-day (…T03:59:59.999Z ≈ midnight US
  // Eastern), so format the date part in UTC to avoid shifting a day earlier in
  // the viewer's timezone — yields the intended "12 months from earned" date.
  function fmtDateUTC(iso) {
    var d = new Date(iso);
    return isNaN(d) ? '' : d.toLocaleDateString('en-US', { timeZone: 'UTC' });
  }
  function relTime(iso) {
    var then = new Date(iso).getTime();
    if (isNaN(then)) return '';
    var days = Math.floor((Date.now() - then) / 86400000);
    if (days < 1) return 'Today';
    if (days < 30) return days + (days === 1 ? ' day ago' : ' days ago');
    var months = Math.floor(days / 30);
    if (months < 12) return months + (months === 1 ? ' month ago' : ' months ago');
    var years = Math.floor(months / 12);
    return years + (years === 1 ? ' year ago' : ' years ago');
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function field(node, name) { return node.querySelector('[data-ll-field="' + name + '"]'); }
  function setText(node, name, value) { var el = field(node, name); if (el) el.textContent = value; }
  function setSrc(node, name, src) { var el = field(node, name); if (el && src) el.setAttribute('src', src); }
  function hideSection(name) {
    var s = ROOT.querySelector('[data-ll-section="' + name + '"]');
    if (s) s.classList.add('ll-hidden');
  }
  function showSection(name) {
    var s = ROOT.querySelector('[data-ll-section="' + name + '"]');
    if (s) s.classList.remove('ll-hidden');
  }
  // Return a clone factory for a grid. Keeps the (hidden) prototype in place and
  // removes any previously-rendered clones, so a grid can be re-rendered safely.
  function gridTemplate(gridName) {
    var grid = ROOT.querySelector('[data-ll-grid="' + gridName + '"]');
    if (!grid) return null;
    var proto = grid.querySelector('[data-ll-tpl]');
    if (!proto) return null;
    Array.prototype.slice.call(grid.children).forEach(function (ch) {
      if (!ch.hasAttribute('data-ll-tpl')) grid.removeChild(ch);
    });
    var base = proto.cloneNode(true);
    base.removeAttribute('data-ll-tpl');
    base.removeAttribute('hidden');
    return {
      grid: grid,
      make: function () { return base.cloneNode(true); }
    };
  }
  // Like gridTemplate, but only removes our own prior clones (tagged
  // data-ll-clone) — leaves siblings such as .ckc-mobile-line in place.
  function tierTemplate(gridName) {
    var grid = ROOT.querySelector('[data-ll-grid="' + gridName + '"]');
    if (!grid) return null;
    var proto = grid.querySelector('[data-ll-tpl]');
    if (!proto) return null;
    Array.prototype.slice.call(grid.querySelectorAll('[data-ll-clone]'))
      .forEach(function (n) { n.parentNode.removeChild(n); });
    var base = proto.cloneNode(true);
    base.removeAttribute('data-ll-tpl');
    base.removeAttribute('hidden');
    base.setAttribute('data-ll-clone', '');
    return { grid: grid, make: function () { return base.cloneNode(true); } };
  }
  // Ordered tier-icon URLs, rendered by Liquid (the JS asset can't call asset_url).
  function tierIcons() {
    var el = ROOT.querySelector('[data-ll-tier-icons]');
    function split(s) { return (s || '').split('|').filter(Boolean); }
    return el
      ? { desktop: split(el.getAttribute('data-desktop')), mobile: split(el.getAttribute('data-mobile')) }
      : { desktop: [], mobile: [] };
  }
  // Position-based icon for tier index i; reuse the last icon for any extras.
  function pickIcon(list, i) {
    if (!list.length) return '';
    return i < list.length ? list[i] : list[list.length - 1];
  }

  /* =========================================================================
     MODAL — lazily-built singleton, appended inside .lp-index so scoped CSS applies.
     open({ title, bodyHtml, variant: 'header'|'centered', bodyClass, onOpen })
     ========================================================================= */
  var _modal = null;
  function getModal() {
    if (_modal) return _modal;
    var wrap = document.createElement('div');
    wrap.className = 'll-modal';
    wrap.setAttribute('hidden', '');
    var overlay = document.createElement('div');
    overlay.className = 'll-modal__overlay';
    overlay.setAttribute('data-close', '');
    var dialog = document.createElement('div');
    dialog.className = 'll-modal__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    wrap.appendChild(overlay);
    wrap.appendChild(dialog);
    ROOT.appendChild(wrap);
    function close() { wrap.setAttribute('hidden', ''); document.removeEventListener('keydown', onKey); }
    function onKey(e) { if (e.key === 'Escape') close(); }
    wrap.addEventListener('click', function (e) { if (e.target.hasAttribute('data-close')) close(); });
    _modal = {
      open: function (opts) {
        if (opts.variant === 'plain') {
          // Floating close button + body only (no title/rule); for fully custom bodies.
          dialog.className = 'll-modal__dialog ll-modal__dialog--centered';
          dialog.innerHTML =
            '<button type="button" class="ll-modal__close ll-modal__close--float" aria-label="Close" data-close>&times;</button>' +
            '<div class="ll-modal__body">' + (opts.bodyHtml || '') + '</div>';
        } else if (opts.variant === 'card') {
          // Left-aligned success card: floating round close + full-width body
          // (celebration image + side-by-side action buttons).
          dialog.className = 'll-modal__dialog ll-modal__dialog--card';
          dialog.innerHTML =
            '<button type="button" class="ll-modal__close ll-modal__close--round" aria-label="Close" data-close>&times;</button>' +
            '<div class="ll-modal__body">' + (opts.bodyHtml || '') + '</div>';
        } else if (opts.variant === 'centered') {
          dialog.className = 'll-modal__dialog ll-modal__dialog--centered';
          dialog.innerHTML =
            '<button type="button" class="ll-modal__close ll-modal__close--float" aria-label="Close" data-close>&times;</button>' +
            '<h3 class="ll-modal__title ll-modal__title--centered">' + escapeHtml(opts.title) + '</h3>' +
            '<div class="ll-modal__rule"></div>' +
            '<div class="ll-modal__body">' + (opts.bodyHtml || '') + '</div>';
        } else {
          dialog.className = 'll-modal__dialog';
          dialog.innerHTML =
            '<div class="ll-modal__head">' +
              '<h3 class="ll-modal__title">' + escapeHtml(opts.title) + '</h3>' +
              '<button type="button" class="ll-modal__close" aria-label="Close" data-close>&times;</button>' +
            '</div>' +
            '<div class="ll-modal__body">' + (opts.bodyHtml || '') + '</div>';
        }
        if (opts.bodyClass) {
          var bodyEl = dialog.querySelector('.ll-modal__body');
          if (bodyEl) bodyEl.className += ' ' + opts.bodyClass;
        }
        wrap.removeAttribute('hidden');
        document.addEventListener('keydown', onKey);
        if (typeof opts.onOpen === 'function') opts.onOpen(dialog, close);
      },
      close: close
    };
    return _modal;
  }

  /* =========================================================================
     DATA / NORMALIZATION — map raw window.loyaltylion data into view values.
     ========================================================================= */
  function currentTierId(customer) {
    return customer && customer.loyaltyTierMembership && customer.loyaltyTierMembership.loyaltyTierId || null;
  }
  function ruleValue(rule, tierId) {
    var cfgs = rule.configurations || [];
    var byTier = null, byDefault = null;
    for (var i = 0; i < cfgs.length; i++) {
      if (!cfgs[i].enabled) continue;
      if (cfgs[i].tierId === tierId) byTier = cfgs[i].value;
      if (cfgs[i].tierId === null) byDefault = cfgs[i].value;
    }
    return byTier != null ? byTier : (byDefault != null ? byDefault : rule.value);
  }
  function rewardCost(reward, program, tierId) {
    var tiers = program.loyaltyTiers || [];
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].id !== tierId) continue;
      var ov = tiers[i].rewardOverrides || [];
      for (var j = 0; j < ov.length; j++) {
        if (ov[j].rewardId === reward.id && ov[j].enabled) return ov[j].cost;
      }
    }
    return reward.point_cost;
  }
  // Is this reward offered at the customer's tier? LoyaltyLion expresses per-tier
  // availability as a rewardOverrides row ({ rewardId, cost, enabled }) on each
  // tier; enabled:false means the tier does not offer that reward at all. This is
  // keyed purely on reward id, so it gates any reward — product or voucher, hat or
  // shirt — that the merchant restricts to a higher tier.
  //
  // Fails open (visible) for a guest with no tier, an unknown tier, or a reward
  // with no override row, so a data gap can never silently empty the grid.
  function rewardEnabledForTier(reward, program, tierId) {
    if (tierId == null) return true;
    var tiers = program.loyaltyTiers || [];
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].id !== tierId) continue;
      var ov = tiers[i].rewardOverrides || [];
      for (var j = 0; j < ov.length; j++) {
        if (ov[j].rewardId === reward.id) return !!ov[j].enabled;
      }
      return true;
    }
    return true;
  }
  // Visible (non-secret) progression tiers, ascending by lower bound.
  function progressTiers(program) {
    return (program.loyaltyTiers || [])
      .filter(function (t) { return !t.hidden && t.name && t.name.indexOf('[') !== 0; })
      .sort(function (a, b) { return (a.bounds.lower || 0) - (b.bounds.lower || 0); });
  }
  // Has the customer already earned/completed this rule (one-time actions)?
  function ruleEarned(rule, customer) {
    if (!customer) return false;
    var cr = customer.completedRules || [];
    for (var i = 0; i < cr.length; i++) if (cr[i].ruleId === rule.id) return true;
    var rc = customer.ruleContext || [];
    for (var j = 0; j < rc.length; j++) if (rc[j].id === rule.id && rc[j].limitReached) return true;
    var ac = customer.actions || [];
    for (var k = 0; k < ac.length; k++) if (ac[k].ruleId === rule.id && ac[k].type === 'rule') return true;
    return false;
  }
  function daysUntilBirthday(bday) {
    var p = (bday || '').split('-');
    if (p.length < 3) return null;
    var month = parseInt(p[1], 10) - 1, day = parseInt(p[2], 10);
    var today = new Date(); today.setHours(0, 0, 0, 0);
    var next = new Date(today.getFullYear(), month, day); next.setHours(0, 0, 0, 0);
    if (next < today) next = new Date(today.getFullYear() + 1, month, day);
    return Math.round((next - today) / 86400000);
  }
  // A claimed reward is still "active" if it's approved, unused and not expired.
  function isActiveReward(cr) {
    if (cr.state && cr.state !== 'approved') return false;
    var r = cr.redeemable || {};
    if (r.fulfilled) return false; // store-fulfilment reward already fulfilled/shipped — no longer active
    if (r.used_at) return false;
    if (r.usage_limit && r.usage_count != null && r.usage_count >= r.usage_limit) return false;
    var exp = r.expires_on || cr.expires_at;
    if (exp) { var t = new Date(exp).getTime(); if (!isNaN(t) && t < Date.now()) return false; }
    return true;
  }
  function historyStatus(state) {
    if (state === 'approved') return { label: 'Approved', cls: 'approved' };
    if (state === 'pending') return { label: 'Pending', cls: 'approved' };
    return { label: 'Void', cls: 'void' }; // void / cancelled / declined
  }
  function rewardExpiryText(reward) {
    var eo = reward.redeemable_expiry_options || reward.expiry_options;
    if (eo && eo.kind && eo.kind !== 'does_not_expire' && eo.seconds) {
      var days = Math.round(eo.seconds / 86400);
      var months = Math.round(days / 30);
      if (days % 30 === 0 && months > 0) return 'Rewards expire ' + months + (months === 1 ? ' month' : ' months') + ' after being issued.';
      return 'Rewards expire ' + days + (days === 1 ? ' day' : ' days') + ' after being issued.';
    }
    if (eo && eo.kind === 'does_not_expire') return 'This reward does not expire.';
    return 'Rewards expire 6 months after being issued.';
  }
  // Decide which button (if any) an earn card shows — mirrors the approved design.
  // Shown on a one-time earn action the customer has already completed: a
  // non-interactive "Claimed Already" button (see the cfg.claimed branch in
  // renderEarn). Repeatable actions like referral never reach this.
  var CLAIMED_CFG = { show: true, text: 'Already Claimed', claimed: true };
  var CHECK_SM = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

  function earnButton(rule, customer) {
    // These actions all require an authenticated customer to complete — hide for guests.
    if (!customer && (
      rule.kind === 'referral' || rule.kind === 'birthday' || rule.kind === 'newsletterSignup' ||
      rule.kind === 'facebookLike' || rule.kind === 'instagramFollow' || rule.kind === 'twitterFollow'
    )) return { show: false };
    switch (rule.kind) {
      case 'purchase':
        return { show: true, text: 'LEARN MORE', modal: true };
      case 'referral':
        return { show: true, text: 'REFER A FRIEND', refer: true };
      case 'birthday':
        if (customer && customer.birthday) {
          var d = daysUntilBirthday(customer.birthday);
          return { show: true, text: (d != null ? d : '') + ' DAYS', disabled: true, clock: true };
        }
        return { show: true, text: 'ADD BIRTHDAY', birthday: true };
      case 'signup':
        if (customer && customer.enrolled) return CLAIMED_CFG;
        return { show: true, text: 'JOIN NOW', href: '/account/register' };
      case 'newsletterSignup':
        if (customer && customer.newsletter && customer.newsletter.signedUp) return CLAIMED_CFG;
        if (ruleEarned(rule, customer)) return CLAIMED_CFG;
        return { show: true, text: 'SIGN UP', newsletter: true };
      case 'facebookLike':
        if (ruleEarned(rule, customer)) return CLAIMED_CFG;
        return { show: true, text: 'COMPLETE ACTIVITY', activity: true, activityName: '$' + RULE_SLUG.facebookLike, href: SOCIAL.facebookLike };
      case 'instagramFollow':
        if (ruleEarned(rule, customer)) return CLAIMED_CFG;
        return { show: true, text: 'COMPLETE ACTIVITY', activity: true, activityName: '$' + RULE_SLUG.instagramFollow, href: SOCIAL.instagramFollow };
      case 'twitterFollow':
        if (ruleEarned(rule, customer)) return CLAIMED_CFG;
        return { show: true, text: 'COMPLETE ACTIVITY', activity: true, activityName: '$' + RULE_SLUG.twitterFollow, href: SOCIAL.twitterFollow };
      default:
        return { show: false };
    }
  }

  /* =========================================================================
     POPUPS — build the modal bodies for active rewards, referrals and redeem confirm.
     ========================================================================= */
  // Popup with full details of a claimed (active) reward.
  // Shared check icon used by the claimed + active-reward popups.
  var CLAIM_CHECK = '<svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="#2c5a50" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 13l4 4L19 7"/></svg>';
  // Small check shown briefly in the copy icon after copying.
  var CLAIM_COPY_DONE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

  // Celebration illustration shown in the claimed-reward success cards. The JS
  // asset can't call asset_url, so the URL is passed on the .lp-index root
  // (see data-ll-claim-image in loyalty-lion-section.liquid).
  function claimImageHtml() {
    var url = ROOT && ROOT.getAttribute('data-ll-claim-image');
    return url ? '<div class="ll-claim__hero"><img src="' + url + '" alt="" /></div>' : '';
  }

  // The theme's cart drawer is a <s-ajaxcart> custom element that listens for
  // these events (see AjaxCart in theme.js). We drive it directly so we can
  // refresh the cart silently (counter only) vs. actually open the drawer.
  function refreshThemeCart() {
    var ac = document.querySelector('s-ajaxcart');
    if (!ac) return;
    // openDrawer:false → re-fetch the cart + update the header counter, but
    // do NOT slide the drawer open.
    try { ac.dispatchEvent(new CustomEvent('ajaxcart:fetch', { detail: { openDrawer: false } })); } catch (e) {}
  }
  function openThemeCart() {
    var ac = document.querySelector('s-ajaxcart');
    if (!ac) return false;
    try { ac.dispatchEvent(new CustomEvent('ajaxcart:open', { detail: { fetch: true } })); return true; } catch (e) { return false; }
  }

  // Shared markup for a claimed reward's code: a full-width dashed pill with the
  // code and a copy icon, plus the full-width "Apply to discount" button.
  function claimCodeBlock(code) {
    if (!code) return '';
    return '<div class="ll-claim__code-row">' +
        '<span class="ll-claim__code-text">' + escapeHtml(code) + '</span>' +
        '<button type="button" class="ll-claim__copy" data-link="' + escapeHtml(code) + '" aria-label="Copy code">' + ICON.copy + '</button>' +
      '</div>' +
      '<button type="button" class="ll-claim__apply" data-code="' + escapeHtml(code) + '">Apply to discount</button>';
  }

  // Copy-to-clipboard + apply-to-cart wiring for the shared claim buttons.
  function wireClaimCopy(dialog) {
    var btn = dialog.querySelector('.ll-claim__copy');
    if (btn) {
      btn.addEventListener('click', function () {
        var link = btn.getAttribute('data-link') || '';
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(link)['catch'](function () {});
        btn.classList.add('ll-claim__copy--done');
        btn.innerHTML = CLAIM_COPY_DONE;
        setTimeout(function () { btn.classList.remove('ll-claim__copy--done'); btn.innerHTML = ICON.copy; }, 1500);
      });
    }
    // Apply the discount code to the cart in the background (no redirect). Only
    // one reward can be active at a time, so block if a free LoyaltyLion product
    // is already in the cart. A code that is already on the cart short-circuits
    // to "Already Applied" without a /cart/update.js round-trip.
    var applyBtn = dialog.querySelector('.ll-claim__apply');
    if (applyBtn) {
      var code = applyBtn.getAttribute('data-code') || '';
      var markApplied = function (label) {
        applyBtn.disabled = true;
        applyBtn.textContent = label;
        applyBtn.classList.add('ll-claim__apply--applied');
      };
      // Reflect the cart's current state as soon as the modal opens, so the user
      // never sees "Apply to discount" for a code that is already on the cart.
      fetchCart().then(function (cart) {
        if (cartHasDiscountCode(cart, code)) markApplied('Already Applied');
      });
      applyBtn.addEventListener('click', function () {
        if (!code || applyBtn.disabled) return;
        applyBtn.disabled = true;
        applyBtn.textContent = 'Applying…';
        clearClaimNote(applyBtn);
        // One cart read serves both guards; re-checked on click because the cart
        // may have changed since the modal opened (another tab, the cart drawer).
        fetchCart().then(function (cart) {
          if (cartHasDiscountCode(cart, code)) {
            markApplied('Already Applied');
            showClaimNote(applyBtn, 'This code is already applied to your cart.', 'ok');
            return;
          }
          if (cartHasClaimedProduct(cart)) {
            applyBtn.disabled = false;
            applyBtn.textContent = 'Apply to discount';
            showClaimNote(applyBtn, 'Only one reward can be claimed at a time. You already have a free product from your rewards in the cart. Remove it before applying this discount.', 'warn');
            return;
          }
          // Setting `discount` on /cart/update.js REPLACES any code already on the
          // cart, so a previously applied rewards code is swapped for this one.
          return fetchWithTimeout('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: code }),
            credentials: 'same-origin'
          })
            .then(function (r) { if (!r.ok) throw new Error('apply_failed'); return r.json(); })
            .then(function () {
              markApplied('Applied ✓');
              showClaimNote(applyBtn, 'Discount applied to your cart.', 'ok');
            })
            .catch(function () {
              applyBtn.disabled = false;
              applyBtn.textContent = 'Apply to discount';
              showClaimNote(applyBtn, 'Could not apply automatically — copy the code and use it at checkout.', 'warn');
            });
        });
      });
    }
  }

  // Read the cart once. Resolves to null on error so callers can treat an
  // unknown cart as "no reason to block".
  function fetchCart() {
    return fetchWithTimeout('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  // True if `code` is already one of the cart's discount codes. Shopify treats
  // discount codes case-insensitively, and reports codes it could not apply
  // (empty cart, unmet minimum) with applicable:false — those do not count.
  function cartHasDiscountCode(cart, code) {
    var want = String(code || '').trim().toUpperCase();
    if (!want) return false;
    return ((cart && cart.discount_codes) || []).some(function (d) {
      return d && d.applicable !== false &&
        String(d.code || '').trim().toUpperCase() === want;
    });
  }

  // True if the cart already holds a free product claimed via LoyaltyLion.
  // Those line items are tagged with the __lion_sfp_id property on add (see
  // redeemProduct's /cart/add.js call).
  function cartHasClaimedProduct(cart) {
    return ((cart && cart.items) || []).some(function (it) {
      var p = it.properties || {};
      return p.__lion_sfp_id != null && p.__lion_sfp_id !== '';
    });
  }

  // Inline status note under the Apply button in the claim modal.
  function showClaimNote(refEl, msg, kind) {
    if (!refEl || !refEl.parentNode) return;
    var note = refEl.parentNode.querySelector('.ll-claim__note');
    if (!note) {
      note = document.createElement('p');
      note.className = 'll-claim__note';
      refEl.parentNode.insertBefore(note, refEl.nextSibling);
    }
    note.className = 'll-claim__note' + (kind ? ' ll-claim__note--' + kind : '');
    note.textContent = msg;
  }
  function clearClaimNote(refEl) {
    if (!refEl || !refEl.parentNode) return;
    var note = refEl.parentNode.querySelector('.ll-claim__note');
    if (note) note.parentNode.removeChild(note);
  }

  // Detail popup for an already-claimed reward — same visual language as the
  // "Reward Claimed!" popup (centered, icon, dashed code pill, green Copy).
  function openActiveReward(cr) {
    var reward = cr.reward || {};
    var r = cr.redeemable || {};
    var st = historyStatus(cr.state);
    var exp = r.expires_on || cr.expires_at;
    var code = r.code;
    var sub = num(cr.point_cost) + ' points · ' + relTime(cr.claimed_at);
    var body =
      '<div class="ll-claim">' +
        '<div class="ll-claim__icon">' + CLAIM_CHECK + '</div>' +
        '<h3 class="ll-claim__title">' + escapeHtml(reward.title || 'Reward') + '</h3>' +
        '<p class="ll-claim__sub">' + escapeHtml(sub) + '</p>' +
        '<span class="ll-claim__status ll-claim__status--' + st.cls + '">' + escapeHtml(st.label) + '</span>' +
        (exp ? '<p class="ll-claim__exp">Expires on: ' + fmtDate(exp) + '</p>' : '') +
        claimCodeBlock(code) +
      '</div>';
    getModal().open({
      variant: 'plain',
      bodyHtml: body,
      onOpen: wireClaimCopy
    });
  }

  // Success popup shown right after a voucher reward is claimed.
  function openRewardClaimed(cr, customer) {
    var reward = cr.reward || {};
    var code = (cr.redeemable && cr.redeemable.code) || '';
    var codeHtml = claimCodeBlock(code);
    var balHtml = (customer && customer.pointsApproved != null)
      ? '<p class="ll-claim__balance">New balance: <strong>' + num(customer.pointsApproved) + ' pts</strong></p>'
      : '';
    var body =
      '<div class="ll-claim ll-claim--card">' +
        '<div class="ll-claim__header"><h3 class="ll-claim__title">Reward Claimed!</h3></div>' +
        '<p class="ll-claim__sub">You redeemed <strong>' + escapeHtml(reward.title || 'your reward') + '</strong>.' +
          (code ? ' Use this code at checkout:' : '') + '</p>' +
        codeHtml +
        balHtml +
        claimImageHtml() +
        '<div class="ll-claim__actions">' +
          '<button type="button" class="ll-claim__btn ll-claim__btn--ghost" data-close>Keep Browsing</button>' +
        '</div>' +
      '</div>';
    getModal().open({
      variant: 'card',
      bodyHtml: body,
      onOpen: wireClaimCopy
    });
  }

  // Success popup shown after a product reward is added to the cart. Stays on the
  // loyalty page (no redirect); user can choose to view the cart.
  function openProductRedeemed(reward, customer) {
    var balHtml = (customer && customer.pointsApproved != null)
      ? '<p class="ll-claim__balance">New balance: <strong>' + num(customer.pointsApproved) + ' pts</strong></p>'
      : '';
    var body =
      '<div class="ll-claim ll-claim--card">' +
        '<div class="ll-claim__header"><h3 class="ll-claim__title">Reward Claimed!</h3></div>' +
        '<p class="ll-claim__sub">We added <strong>' + escapeHtml(reward.title || 'your reward') + '</strong> to your cart.</p>' +
        balHtml +
        claimImageHtml() +
        '<div class="ll-claim__actions">' +
          '<button type="button" class="ll-claim__btn ll-claim__btn--ghost" data-close>Keep Browsing</button>' +
          '<a class="ll-claim__btn ll-claim__btn--primary" href="/cart" data-view-cart>View Cart</a>' +
        '</div>' +
      '</div>';
    getModal().open({
      variant: 'card',
      bodyHtml: body,
      onOpen: function (dialog, close) {
        var vc = dialog.querySelector('[data-view-cart]');
        if (!vc) return;
        vc.addEventListener('click', function (e) {
          // Close the popup and slide open the theme cart drawer instead of
          // navigating to /cart. Fall back to /cart if the drawer isn't present.
          e.preventDefault();
          close();
          if (!openThemeCart()) window.location.href = '/cart';
        });
      }
    });
  }

  // Show a product reward's terms (its `description`) in a simple header popup.
  function openTerms(reward) {
    getModal().open({
      variant: 'header',
      title: reward.title || 'Terms and Conditions',
      bodyHtml: '<p class="pfp__terms-text">' + escapeHtml(reward.description || '') + '</p>'
    });
  }

  // Capture the customer's birthday and save it via the SDK so they earn the reward.
  function openBirthdayModal(points) {
    var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    var opts = '<option value="" disabled selected>Month</option>';
    for (var m = 0; m < MONTHS.length; m++) opts += '<option value="' + (m + 1) + '">' + MONTHS[m] + '</option>';
    var ptsTxt = num(points) + ' points';
    var body =
      '<div class="ll-bday">' +
        '<p class="ll-bday__intro">Enter your birthday below and you\'ll get ' + ptsTxt + ' on your next birthday</p>' +
        '<div class="ll-bday__row">' +
          '<select class="ll-bday__select" data-bday-month aria-label="Month">' + opts + '</select>' +
          '<input type="text" class="ll-bday__input" data-bday-day inputmode="numeric" maxlength="2" placeholder="Day" aria-label="Day" />' +
          '<input type="text" class="ll-bday__input" data-bday-year inputmode="numeric" maxlength="4" placeholder="Year" aria-label="Year" />' +
        '</div>' +
        '<p class="ll-bday__status" data-bday-status hidden></p>' +
        '<button type="button" class="ll-bday__save" data-bday-save>SAVE BIRTHDAY</button>' +
      '</div>';
    getModal().open({
      variant: 'header',
      title: 'Happy Birthday',
      bodyHtml: body,
      onOpen: function (dialog) {
        var monthEl = dialog.querySelector('[data-bday-month]');
        var dayEl = dialog.querySelector('[data-bday-day]');
        var yearEl = dialog.querySelector('[data-bday-year]');
        var statusEl = dialog.querySelector('[data-bday-status]');
        var saveEl = dialog.querySelector('[data-bday-save]');
        function showError(msg) {
          statusEl.textContent = msg;
          statusEl.className = 'll-bday__status ll-bday__status--error';
          statusEl.removeAttribute('hidden');
        }
        function submit() {
          if (saveEl.disabled) return; // in-flight guard — blocks double-submit (click or Enter)
          var month = parseInt(monthEl.value, 10);
          var dayRaw = dayEl.value.trim(), yearRaw = yearEl.value.trim();
          var thisYear = new Date().getFullYear();
          if (!month) return showError('Please choose a month.');
          if (!/^\d{1,2}$/.test(dayRaw)) return showError('Please enter a valid day.');
          if (!/^\d{4}$/.test(yearRaw)) return showError('Please enter a valid year.');
          var day = parseInt(dayRaw, 10);
          var year = parseInt(yearRaw, 10);
          if (day < 1 || day > 31) return showError('Please enter a valid day.');
          if (year < 1900 || year > thisYear) return showError('Please enter a valid year.');
          // Reject impossible dates (e.g. Feb 30) by round-tripping through Date.
          var check = new Date(year, month - 1, day);
          if (check.getFullYear() !== year || check.getMonth() !== month - 1 || check.getDate() !== day) {
            return showError('Please enter a valid date.');
          }
          var date = year + '-' + (month < 10 ? '0' + month : month) + '-' + (day < 10 ? '0' + day : day);
          statusEl.setAttribute('hidden', '');
          saveEl.textContent = 'SAVING…';
          saveEl.disabled = true;
          saveEl.style.opacity = '0.7';
          updateBirthday(date, function () {
            if (STATE.customer) STATE.customer.birthday = date;
            try { renderEarn(STATE.customer, STATE.program); } catch (e) {}
            var form = dialog.querySelector('.ll-bday');
            if (form) form.innerHTML = '<p class="ll-bday__success">Saved! You\'ll get ' + ptsTxt + ' on your next birthday</p>';
          }, function () {
            saveEl.textContent = 'SAVE BIRTHDAY';
            saveEl.disabled = false;
            saveEl.style.opacity = '';
            showError('Sorry, we could not save your birthday. Please try again.');
          });
        }
        saveEl.addEventListener('click', submit);
        [dayEl, yearEl].forEach(function (el) {
          el.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
          });
        });
      }
    });
  }

  function buildReferralBody(customer, program, points) {
    var urls = (customer && customer.referralUrls) || {};
    var direct = urls.direct || '';
    var ri = program.refereeIncentive || {};
    var disc = ri.discountAmount != null ? ri.discountAmount : 10;
    var minSpend = ri.minimumSpend != null ? ri.minimumSpend : 30;
    if (minSpend > 1000) minSpend = Math.round(minSpend / 100); // tolerate cents
    var appId = program.settings && program.settings.merchantFacebookAppId;
    var messenger = appId
      ? 'https://www.facebook.com/dialog/send?app_id=' + encodeURIComponent(appId) +
        '&link=' + encodeURIComponent(direct) + '&redirect_uri=' + encodeURIComponent(direct)
      : (urls.facebook || '#');

    var buttons = [
      { cls: 'twitter', label: 'Post on X', href: urls.twitter, icon: ICON.twitter },
      { cls: 'whatsapp', label: 'Share on WhatsApp', href: urls.whatsapp, icon: ICON.whatsapp },
      { cls: 'messenger', label: 'Send via Messenger', href: messenger, icon: ICON.messenger },
      { cls: 'facebook', label: 'Share on Facebook', href: urls.facebook, icon: ICON.facebook },
      { cls: 'email', label: 'Share via email', href: urls.email, icon: ICON.email }
    ].map(function (b) {
      return '<a class="ll-share ll-share--' + b.cls + '" href="' + escapeHtml(b.href || '#') + '"' +
        ' target="_blank" rel="noopener noreferrer" aria-label="' + escapeHtml(b.label) + '">' + b.icon + '</a>';
    }).join('');

    return '<p class="ll-ref__desc">Give a friend $' + disc + ' off their first purchase and earn ' +
        num(points) + ' points if they spend over $' + minSpend + '</p>' +
      '<div class="ll-share-row">' + buttons + '</div>' +
      '<p class="ll-ref__copy-label">Or copy your link and share it anywhere</p>' +
      '<div class="ll-ref__copy">' +
        '<input class="ll-ref__input" type="text" readonly value="' + escapeHtml(direct) + '" />' +
        '<button type="button" class="ll-ref__copy-btn" aria-label="Copy link" data-link="' + escapeHtml(direct) + '">' + ICON.copy + '</button>' +
      '</div>';
  }

  function wireReferral(dialog) {
    var btn = dialog.querySelector('.ll-ref__copy-btn');
    var input = dialog.querySelector('.ll-ref__input');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var link = btn.getAttribute('data-link') || '';
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link)['catch'](function () {});
      } else if (input) {
        input.select();
        try { document.execCommand('copy'); } catch (e) {}
      }
      btn.classList.add('ll-ref__copy-btn--done');
      setTimeout(function () { btn.classList.remove('ll-ref__copy-btn--done'); }, 1500);
    });
  }

  // Confirmation popup shown when an affordable reward is clicked. Handles both
  // voucher/discount rewards and product rewards (with a size selector).
  function openRedeemConfirm(reward, cost) {
    var isProduct = reward.kind === 'product_cart';
    var variants = isProduct ? (reward.target_products || []) : [];

    var variantHtml = '';
    if (variants.length > 1) {
      variantHtml = '<div class="ll-redeem__variant"><label class="ll-redeem__variant-label">Size</label>' +
        '<select class="ll-redeem__select">' +
        variants.map(function (v) {
          return '<option value="' + escapeHtml(v.variant_id) + '" data-product="' + escapeHtml(v.id) + '">' + escapeHtml(v.title || '') + '</option>';
        }).join('') +
        '</select></div>';
    }
    var expLine = isProduct ? '' : '<p class="ll-redeem__exp">' + escapeHtml(rewardExpiryText(reward)) + '</p>';

    var body =
      '<p class="ll-redeem__q">Redeem ' + num(cost) + ' points for this reward?</p>' +
      expLine +
      variantHtml +
      '<div class="ll-redeem__actions">' +
        '<button type="button" class="ll-redeem__btn ll-redeem__btn--ghost" data-close>NOT NOW</button>' +
        '<button type="button" class="ll-redeem__btn ll-redeem__btn--primary" data-redeem>REDEEM</button>' +
      '</div>';

    getModal().open({
      variant: 'header',
      title: reward.title || 'Redeem reward',
      bodyHtml: body,
      onOpen: function (dialog, close) {
        var rb = dialog.querySelector('[data-redeem]');
        if (!rb) return;
        // Mark sold-out sizes in the dropdown, and default to the first in-stock
        // one. Fail open: unknown variants stay selectable. (Mirrors the button
        // grid in openProductPreview.)
        var selEl = dialog.querySelector('.ll-redeem__select');
        if (selEl && variants[0] && variants[0].handle) {
          fetchProduct(variants[0].handle).then(function (data) {
            var avail = (data && data.avail) || {};
            var firstAvailable = -1;
            for (var k = 0; k < selEl.options.length; k++) {
              var opt = selEl.options[k];
              if (avail[String(opt.value)] === false) {
                opt.disabled = true;
                opt.textContent = opt.textContent + ' (Sold out)';
              } else if (firstAvailable < 0) {
                firstAvailable = k;
              }
            }
            if (firstAvailable >= 0 && selEl.options[selEl.selectedIndex] && selEl.options[selEl.selectedIndex].disabled) {
              selEl.selectedIndex = firstAvailable;
            }
          });
        }
        rb.addEventListener('click', function () {
          if (isProduct) {
            var sel = dialog.querySelector('.ll-redeem__select');
            var first = variants[0] || {};
            var variantId = first.variant_id, productId = first.id;
            if (sel) {
              variantId = sel.value;
              productId = sel.options[sel.selectedIndex].getAttribute('data-product');
            }
            redeemProduct(reward, productId, variantId, rb);
          } else {
            redeemReward(reward, cost, close);
          }
        });
      }
    });
  }

  /* =========================================================================
     REDEMPTION — claim vouchers/products via the LoyaltyLion SDK.
     ========================================================================= */
  // Both of these gate a redeem button, so they must always settle — a hung
  // /cart.js would otherwise strand redeemProduct before it even starts.
  function getCartToken() {
    return fetchWithTimeout('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { return c.token; })
      .catch(function () { return ''; });
  }

  // Cart subtotal in cents, for product-reward minimum-spend requirements.
  function getCartSubtotal() {
    return fetchWithTimeout('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { return c.items_subtotal_price || c.total_price || 0; })
      .catch(function () { return 0; });
  }

  // Claim a voucher/discount reward, then show the issued code to copy.
  function redeemReward(reward, cost, close) {
    var L = window.loyaltylion;
    if (!L || !L.api || !L.api.authPacket || !L.token) {
      alert('Unable to redeem right now. Please refresh and try again.');
      return;
    }
    var rb = ROOT.querySelector('.ll-redeem__btn--primary');
    if (rb) { rb.textContent = 'REDEEMING…'; rb.disabled = true; rb.style.opacity = '0.7'; }

    fetchWithTimeout('https://sdk.loyaltylion.net/customers/rewards/claim', {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'content-type': 'application/json',
        'x-auth-packet': L.api.authPacket,
        'x-sdk-version': '2023-11',
        'x-site-token': L.token
      },
      body: JSON.stringify({
        reward_id: reward.id,
        source: { interface: 'loyalty_page', component: 'rewards_list', surface: 'storefront' }
      }),
      mode: 'cors',
      credentials: 'omit'
    })
      .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
      .then(function (r) {
        if (!r.ok || !r.data || !r.data.claimed_reward) throw new Error('claim_failed');
        var data = r.data;
        // Refresh local state + affected sections with the server's updated customer.
        if (data.customer) {
          STATE.customer = data.customer;
          STATE.tierId = currentTierId(STATE.customer);
          try { renderHero(STATE.customer); } catch (e) {}
          try { renderActiveRewards(STATE.customer); } catch (e) {}
          try { renderRedeemMerged(STATE.customer, STATE.program, STATE.tierId); } catch (e) {}
        }
        // Show the "Reward Claimed!" success popup with the issued code + new balance.
        openRewardClaimed(data.claimed_reward, data.customer || STATE.customer);
      })
      .catch(function (e) {
        console.error('[loyalty-lion] redeem error', e);
        if (rb) { rb.textContent = 'REDEEM'; rb.disabled = false; rb.style.opacity = ''; }
        // A timeout means the claim may have gone through server-side and only
        // the response was lost, so don't invite a blind retry that could spend
        // the points twice.
        alert(isTimeoutError(e)
          ? 'Your connection timed out before we could confirm this. Please refresh the page and check Active Rewards before trying again.'
          : 'Sorry, we could not redeem this reward. Please try again.');
      });
  }

  // Save the customer's birthday so LoyaltyLion can award the birthday reward.
  function updateBirthday(date, onOk, onErr) {
    var L = window.loyaltylion;
    if (!L || !L.api || !L.api.authPacket || !L.token) {
      if (onErr) onErr();
      return;
    }
    fetchWithTimeout('https://sdk.loyaltylion.net/customers/update/birthday', {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'content-type': 'application/json',
        'x-auth-packet': L.api.authPacket,
        'x-sdk-version': '2023-11',
        'x-site-token': L.token
      },
      body: JSON.stringify({ birthday: date }),
      mode: 'cors',
      credentials: 'omit'
    })
      .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
      .then(function (r) {
        if (!r.ok || !r.data || !r.data.updated) throw new Error('birthday_update_failed');
        if (onOk) onOk(r.data);
      })
      .catch(function (e) {
        console.error('[loyalty-lion] birthday update error', e);
        if (onErr) onErr();
      });
  }

  // Sign the customer up to the newsletter so LoyaltyLion awards the signup reward.
  function signupNewsletter(onOk, onErr) {
    var L = window.loyaltylion;
    if (!L || !L.api || !L.api.authPacket || !L.token) {
      if (onErr) onErr();
      return;
    }
    fetchWithTimeout('https://sdk.loyaltylion.net/customers/update/newsletter-signup', {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'x-auth-packet': L.api.authPacket,
        'x-sdk-version': '2023-11',
        'x-site-token': L.token
      },
      mode: 'cors',
      credentials: 'omit'
    })
      .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
      .then(function (r) {
        if (!r.ok || !r.data || !r.data.customer) throw new Error('newsletter_signup_failed');
        STATE.customer = r.data.customer;
        STATE.tierId = currentTierId(STATE.customer);
        if (onOk) onOk(r.data);
      })
      .catch(function (e) {
        console.error('[loyalty-lion] newsletter signup error', e);
        if (onErr) onErr();
      });
  }

  // Track a social activity (Facebook like, Instagram/X follow) to award its points.
  function trackActivity(name, url, onOk, onErr) {
    var L = window.loyaltylion;
    if (!L || !L.api || !L.api.authPacket || !L.token) {
      if (onErr) onErr();
      return;
    }
    fetchWithTimeout('https://sdk.loyaltylion.net/customers/activities', {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'content-type': 'application/json',
        'x-auth-packet': L.api.authPacket,
        'x-sdk-version': '2023-11',
        'x-site-token': L.token
      },
      body: JSON.stringify({ name: name, properties: { url: url } }),
      mode: 'cors',
      credentials: 'omit'
    })
      .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
      .then(function (r) {
        if (!r.ok || !r.data || !r.data.customer) throw new Error('activity_failed');
        STATE.customer = r.data.customer;
        STATE.tierId = currentTierId(STATE.customer);
        if (onOk) onOk(r.data);
      })
      .catch(function (e) {
        console.error('[loyalty-lion] activity error', e);
        if (onErr) onErr();
      });
  }

  // Claim a product reward, add the issued variant to the cart, then go to cart.
  function redeemProduct(reward, productId, variantId, rb) {
    var L = window.loyaltylion;
    if (!L || !L.api || !L.api.authPacket || !L.token || !STATE.customer) {
      alert('Unable to redeem right now. Please refresh and try again.');
      return;
    }
    if (rb) { rb.textContent = 'REDEEMING…'; rb.disabled = true; rb.style.opacity = '0.7'; }
    var claimedCustomer = null;
    var merchantId = String(STATE.customer.merchantId);
    productId = String(productId);
    variantId = String(variantId);

    getCartToken().then(function (cartId) {
      return fetchWithTimeout('https://sdk.loyaltylion.net/customers/shopify_function_rewards/redeem', {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'content-type': 'application/json',
          'x-auth-packet': L.api.authPacket,
          'x-sdk-version': '2023-11',
          'x-site-token': L.token
        },
        body: JSON.stringify({
          rewardId: reward.id,
          product: { productId: productId, variantId: variantId },
          contextParams: {
            action: 'free_product',
            customerMerchantId: merchantId,
            productId: productId,
            variantId: variantId,
            cartId: cartId
          },
          cartId: cartId
        }),
        mode: 'cors',
        credentials: 'omit'
      }).then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); });
    }).then(function (r) {
      if (!r.ok || !r.data || !r.data.claimedReward) throw new Error('product_redeem_failed');
      claimedCustomer = r.data.customer || null;
      var redeemable = r.data.claimedReward.redeemable || {};
      var redeemedVariantId = redeemable.product && redeemable.product.variant_id;
      var contextCode = redeemable.shopify_cart_line_item_context;
      return fetchWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ quantity: 1, id: redeemedVariantId, properties: { __lion_sfp_id: contextCode } }),
        credentials: 'include'
      });
    }).then(function (addRes) {
      if (!addRes.ok) throw new Error('cart_add_failed');
      // Stay on the loyalty page — no redirect to /cart.
      if (claimedCustomer) {
        STATE.customer = claimedCustomer;
        STATE.tierId = currentTierId(STATE.customer);
        try { renderHero(STATE.customer); } catch (e) {}
        try { renderActiveRewards(STATE.customer); } catch (e) {}
        try { renderRedeemMerged(STATE.customer, STATE.program, STATE.tierId); } catch (e) {}
      }
      // Refresh the theme cart + header counter WITHOUT opening the cart drawer.
      // (Dispatching 'cart:add:complete' would auto-open the drawer; we only
      // want it to open from the popup's "View Cart" button.)
      refreshThemeCart();
      // Close the reward preview (if the redeem came from the combined popup),
      // then show the "Reward Claimed!" success popup.
      closeRewardPreview();
      openProductRedeemed(reward, claimedCustomer || STATE.customer);
    })['catch'](function (e) {
      console.error('[loyalty-lion] product redeem error', e);
      if (rb) { rb.textContent = 'REDEEM'; rb.disabled = false; rb.style.opacity = ''; }
      // As in redeemReward: a timeout may mean the claim succeeded and only the
      // response was lost, so point the user at their cart instead of a retry.
      alert(isTimeoutError(e)
        ? 'Your connection timed out before we could confirm this. Please refresh the page and check your cart and Active Rewards before trying again.'
        : 'Sorry, we could not redeem this product. Please try again.');
    });
  }


  /* =========================================================================
     RENDERERS — one per section; each is a no-op (and stays hidden) without data.
     ========================================================================= */
  // Greatest common divisor (used to shrink the knapsack table below).
  function gcd(a, b) { while (b) { var t = b; b = a % b; a = t; } return a; }

  // The most dollars the customer could actually redeem RIGHT NOW: the best
  // combination of affordable flat cart-discount vouchers (each redeemable any
  // number of times) whose total point cost fits the balance. Powers the hero
  // "worth" — an honest, redeemable figure rather than a linear extrapolation.
  function redeemableWorth(program, balance) {
    var vouchers = [];
    (program.rewards || []).forEach(function (r) {
      if (r.kind === 'cart_discount_voucher' && r.discount_type === 'flat' && r.discount_amount > 0 && r.point_cost > 0) {
        vouchers.push({ cost: r.point_cost, value: r.discount_amount });
      }
    });
    if (!vouchers.length || balance <= 0) return 0;

    // Collapse the table by the GCD of all point costs: combinations only ever
    // spend multiples of it, so this rounds the balance down to what's spendable
    // and keeps real (round-numbered) denominations to a tiny capacity.
    var g = 0;
    for (var i = 0; i < vouchers.length; i++) g = gcd(g, vouchers[i].cost);
    var cap = Math.floor(balance / g);

    // Defensive fallback for pathological denominations (e.g. coprime costs)
    // that would blow up the DP: greedy by best value-per-point, then top up.
    if (cap > 20000) {
      var byRate = vouchers.slice().sort(function (a, b) { return (b.value / b.cost) - (a.value / a.cost); });
      var rem = balance, worth = 0;
      for (var k = 0; k < byRate.length; k++) {
        var n = Math.floor(rem / byRate[k].cost);
        worth += n * byRate[k].value;
        rem -= n * byRate[k].cost;
      }
      return worth;
    }

    // Unbounded knapsack: dp[c] = max redeemable value using c*g points.
    var dp = new Array(cap + 1);
    dp[0] = 0;
    for (var c = 1; c <= cap; c++) {
      dp[c] = dp[c - 1]; // carry forward (spending fewer points is always allowed)
      for (var v = 0; v < vouchers.length; v++) {
        var w = vouchers[v].cost / g;
        if (w <= c && dp[c - w] + vouchers[v].value > dp[c]) dp[c] = dp[c - w] + vouchers[v].value;
      }
    }
    return dp[cap];
  }
  // The tier the customer currently sits in (by membership id, else by spend).
  function currentTier(customer, program) {
    var tiers = progressTiers(program);
    if (!tiers.length) return null;
    var id = currentTierId(customer);
    for (var i = 0; i < tiers.length; i++) if (tiers[i].id === id) return tiers[i];
    var spend = (customer && customer.loyaltyTierEligibleSpend) || 0, cur = tiers[0];
    for (var j = 0; j < tiers.length; j++) if ((tiers[j].bounds.lower || 0) <= spend) cur = tiers[j];
    return cur;
  }
  // Purchase points-per-$1 at a given tier (for the hero/tier badges).
  function purchaseRate(program, tierId) {
    var rules = program.rules || [];
    for (var i = 0; i < rules.length; i++) if (rules[i].kind === 'purchase') return ruleValue(rules[i], tierId);
    return null;
  }

  function renderHero(customer) {
    if (!customer || customer.pointsApproved == null) return; // stays hidden — no placeholder
    var program = STATE.program || {}, sym = STATE.sym || '$';
    var els = ROOT.querySelectorAll('[data-ll="hero-points"], [data-lion-points]');
    for (var i = 0; i < els.length; i++) els[i].textContent = num(customer.pointsApproved);

    // Tier badge: "<TIER> MEMBER · <rate> PTS / $1"
    var tier = currentTier(customer, program);
    var badge = ROOT.querySelector('[data-ll="hero-tier-badge"]');
    var badgeWrap = ROOT.querySelector('[data-ll="hero-tier-wrap"]');
    if (badge && tier) {
      var rate = purchaseRate(program, tier.id);
      var txt = tier.name.toUpperCase() + ' MEMBER';
      if (rate != null) txt += ' · ' + num(rate) + ' PTS / $1';
      badge.textContent = txt;
      if (badgeWrap) badgeWrap.removeAttribute('hidden');
    }

    showSection('hero-points');
  }

  // Smallest threshold the customer has not yet reached (the "next" tier), or Infinity.
  function firstUnreached(sorted, spend) {
    for (var i = 0; i < sorted.length; i++) if (spend < sorted[i]) return sorted[i];
    return Infinity;
  }
  // Progress %, spread evenly across the milestone nodes.
  function fillPct(sorted, spend) {
    var n = sorted.length;
    if (n < 2) return spend > 0 ? 100 : 0;
    if (spend >= sorted[n - 1]) return 100;
    for (var k = 0; k < n - 1; k++) {
      if (spend >= sorted[k] && spend < sorted[k + 1]) {
        var seg = (spend - sorted[k]) / (sorted[k + 1] - sorted[k]);
        return ((k + seg) / (n - 1)) * 100;
      }
    }
    return 0;
  }

  // Paint dot/text state per row; add the "$X to go" pill on the next mobile tier.
  function paintTierRows(rows, isMobile, spend, nextThr, sym) {
    for (var i = 0; i < rows.length; i++) {
      var thr = parseInt(rows[i].getAttribute('data-ll-threshold'), 10) || 0;
      var nextRowThr = (i + 1 < rows.length) ? (parseInt(rows[i + 1].getAttribute('data-ll-threshold'), 10) || Infinity) : Infinity;
      var reached = spend >= thr;
      var isCurrent = reached && spend < nextRowThr; // highest reached tier
      var dot = rows[i].querySelector('.ckc-dot');
      if (dot) {
        dot.classList.remove('completed', 'active', 'inactive');
        dot.classList.add(reached ? (isCurrent ? 'active' : 'completed') : 'inactive');
      }
      var amtEl = rows[i].querySelector('.ckc-node-amount');
      var nameEl = rows[i].querySelector('.ckc-tier-name');
      if (amtEl) amtEl.classList.toggle('muted', !reached);
      if (nameEl) nameEl.classList.toggle('muted', !reached);
      if (isMobile) {
        var old = rows[i].querySelector('.ckc-goto-pill');
        if (old) old.parentNode.removeChild(old);
        if (!reached && thr === nextThr) {
          var pill = document.createElement('span');
          pill.className = 'ckc-goto-pill';
          pill.textContent = moneyDual(thr - spend, sym) + ' to go';
          rows[i].appendChild(pill);
        }
      }
    }
  }

  function renderTierStatus(customer, program, sym) {
    if (!customer) { hideSection('tier-status'); return; }
    var spend = customer.loyaltyTierEligibleSpend || 0;
    var ptiers = progressTiers(program);
    if (!ptiers.length) { hideSection('tier-status'); return; } // no tiers — nothing to show
    var icons = tierIcons();

    // Build one grid (desktop or mobile) from the real program tiers: name,
    // price (threshold), and a position-based icon. Returns thresholds + clones.
    function buildGrid(gridName, iconList) {
      var tpl = tierTemplate(gridName);
      if (!tpl) return { thresholds: [], rows: [] };
      var thresholds = [];
      ptiers.forEach(function (tier, i) {
        var node = tpl.make(), thr = tier.bounds.lower || 0;
        thresholds.push(thr);
        node.setAttribute('data-ll-threshold', thr);
        setText(node, 'amount', moneyDual(thr, sym));
        setText(node, 'name', tier.name);
        var iconEl = field(node, 'icon');
        if (iconEl) {
          var src = pickIcon(iconList, i);
          if (src) iconEl.setAttribute('src', src); else iconEl.style.display = 'none';
        }
        tpl.grid.appendChild(node);
      });
      return { thresholds: thresholds, rows: tpl.grid.querySelectorAll('[data-ll-clone]') };
    }

    var dGrid = buildGrid('tier-desktop', icons.desktop);
    var mGrid = buildGrid('tier-mobile', icons.mobile);
    var thresholds = dGrid.thresholds.length ? dGrid.thresholds : mGrid.thresholds;
    var sorted = thresholds.slice().sort(function (a, b) { return a - b; });
    // Position by the customer's actual tier membership, not raw spend: a member
    // placed in a tier (e.g. Explorer) sits on that tier even with $0 eligible
    // spend. The current tier's lower bound is a floor for the marker/fill/dots.
    var curTier = currentTier(customer, program);
    var tierFloor = (curTier && curTier.bounds && curTier.bounds.lower) || 0;
    var posSpend = Math.max(spend, tierFloor);
    var nextThr = firstUnreached(sorted, posSpend);

    // Position desktop dots at even index percentages so they share the fill's
    // coordinate system (first dot at 0%, last at 100%, matching fillPct()).
    var desktop = dGrid.rows, dn = desktop.length;
    for (var d = 0; d < dn; d++) {
      desktop[d].style.left = (dn > 1 ? (d / (dn - 1)) * 100 : 0).toFixed(4) + '%';
    }

    paintTierRows(desktop, false, posSpend, nextThr, sym);
    paintTierRows(mGrid.rows, true, posSpend, nextThr, sym);

    // Fill the desktop track + you-here marker + mobile timeline line.
    var pct = fillPct(sorted, posSpend);
    var fill = ROOT.querySelector('.ckc-track-fill'); if (fill) fill.style.width = pct.toFixed(2) + '%';
    var here = ROOT.querySelector('.ckc-you-here'); if (here) here.style.left = pct.toFixed(2) + '%';
    var mfill = ROOT.querySelector('.ckc-mobile-line-fill'); if (mfill) mfill.style.height = pct.toFixed(2) + '%';

    // Current-tier badge.
    var tier = curTier;
    var badge = ROOT.querySelector('[data-ll="tier-badge"]');
    if (badge && tier) {
      var rate = purchaseRate(program, tier.id);
      badge.textContent = tier.name + (rate != null ? ' · ' + num(rate) + ' pts / $1' : '');
    }

    // Subtitle: next tier to reach, or a top-tier message.
    var sub = ROOT.querySelector('[data-ll="tier-subtitle"]');
    if (sub) {
      var next = null;
      for (var t = 0; t < ptiers.length; t++) { if ((ptiers[t].bounds.lower || 0) > posSpend) { next = ptiers[t]; break; } }
      if (next) {
        var nrate = purchaseRate(program, next.id);
        sub.innerHTML = "You've spent <strong>" + moneyDual(posSpend, sym) + "</strong> so far. Spend <strong>" +
          moneyDual(next.bounds.lower - posSpend, sym) + " more</strong> to reach <strong>" + escapeHtml(next.name) + "</strong>" +
          (nrate != null ? ' and earn ' + num(nrate) + ' pts / $1.' : '.');
      } else {
        var top = ptiers[ptiers.length - 1];
        sub.innerHTML = top ? "You've reached <strong>" + escapeHtml(top.name) + '</strong>!' : "You've reached the top!";
      }
    }
    showSection('tier-status');
  }

  // The big circular badge value on an active-reward card.
  function activeBadge(reward) {
    if (reward.kind === 'free_shipping_voucher') return { text: 'FREE SHIP', free: true };
    if (reward.kind === 'cart_discount_voucher' && reward.discount_type === 'flat') return { text: '$' + reward.discount_amount };
    if (reward.kind === 'cart_discount_voucher' && reward.discount_type === 'percentage') return { text: reward.discount_amount + '%' };
    // Products / unknown rewards: just the first initial so it fits the circle.
    var t = (reward.title || 'Reward').trim();
    return { text: (t.charAt(0) || 'R').toUpperCase(), initial: true };
  }
  // The status pill: EXPIRES IN Xd / NEW / ACTIVE.
  function activeStatus(cr) {
    var r = cr.redeemable || {};
    var exp = r.expires_on || cr.expires_at;
    if (exp) {
      var days = Math.ceil((new Date(exp).getTime() - Date.now()) / 86400000);
      if (!isNaN(days) && days >= 0 && days <= 14) return 'EXPIRES IN ' + days + 'D';
    }
    var claimed = new Date(cr.claimed_at).getTime();
    if (!isNaN(claimed) && (Date.now() - claimed) < 3 * 86400000) return 'NEW';
    return 'ACTIVE';
  }
  function renderActiveRewards(customer) {
    var active = ((customer && customer.claimedRewards) || [])
      .filter(isActiveReward)
      .sort(function (a, b) { return new Date(b.claimed_at) - new Date(a.claimed_at); });
    if (!active.length) { hideSection('active-rewards'); return; }
    var tpl = gridTemplate('active-rewards');
    if (!tpl) return;
    active.forEach(function (cr) {
      var reward = cr.reward || {};
      var node = tpl.make();
      var b = activeBadge(reward);
      var badgeEl = field(node, 'badge');
      if (badgeEl) {
        badgeEl.textContent = b.text;
        if (b.free) badgeEl.classList.add('free-ship');
        if (b.initial) badgeEl.classList.add('reward-card__badge-val--initial');
      }
      setText(node, 'name', reward.title || 'Reward');
      var code = cr.redeemable && cr.redeemable.code;
      setText(node, 'meta', code ? 'Code: ' + code : relTime(cr.claimed_at));
      var st = activeStatus(cr);
      var stEl = field(node, 'status');
      if (stEl) {
        stEl.textContent = st;
        stEl.className = 'reward-card__status reward-card__status--' +
          (st.indexOf('EXPIRES') === 0 ? 'expires' : (st === 'NEW' ? 'new' : 'active'));
      }
      node.style.cursor = 'pointer';
      node.addEventListener('click', function (e) { e.preventDefault(); openActiveReward(cr); });
      tpl.grid.appendChild(node);
    });
    showSection('active-rewards');
  }

  // Populate the static "Climb the Ranks" tier comparison cards with live
  // LoyaltyLion values (rate, spend threshold, birthday + referral bonuses).
  // Cards are matched to program tiers by their data-ll-tier-card order index;
  // the redacted teaser card has no hook and stays static. Hardcoded values in
  // the markup act as fallbacks if data is missing.
  function renderTierTable(customer, program) {
    var cards = ROOT.querySelectorAll('[data-ll-tier-card]');
    if (!cards.length) return;
    var tiers = progressTiers(program), sym = STATE.sym || '$';
    var curId = currentTierId(customer);
    var rules = program.rules || [];
    function ruleByKind(k) {
      for (var i = 0; i < rules.length; i++) {
        var r = rules[i];
        if (r.kind === k && r.enabled && !r.deactivated && !r.deleted) return r;
      }
      return null;
    }
    var bRule = ruleByKind('birthday'), rRule = ruleByKind('referral');
    Array.prototype.forEach.call(cards, function (card) {
      var tier = tiers[parseInt(card.getAttribute('data-ll-tier-card'), 10) || 0];
      if (!tier) return; // fewer LL tiers than cards -> leave static fallback
      var rate = purchaseRate(program, tier.id);
      if (rate != null) setText(card, 'rate', num(rate) + ' points per $1');
      var lower = (tier.bounds && tier.bounds.lower) || 0;
      setText(card, 'sub', lower > 0 ? 'Spend ' + moneyDual(lower, sym) : 'Start here');
      if (bRule) setText(card, 'birthday', num(ruleValue(bRule, tier.id)));
      if (rRule) setText(card, 'referral', num(ruleValue(rRule, tier.id)));

      // Flag the customer's current tier with a "You are here" badge. (Runs
      // idempotently — clear any prior badge/flag before re-stamping.)
      var isCurrent = curId != null && tier.id === curId;
      card.classList.toggle('tier-card--current', isCurrent);
      var head = card.querySelector('.tier-card__head');
      var prior = head && head.querySelector('[data-ll-here]');
      if (prior) prior.parentNode.removeChild(prior);
      if (isCurrent && head) {
        var badge = document.createElement('span');
        badge.className = 'tier-card__here';
        badge.setAttribute('data-ll-here', '');
        badge.textContent = 'You are here';
        head.insertBefore(badge, head.firstChild);
      }
    });
  }

  function renderEarn(customer, program) {
    var tpl = gridTemplate('earn');
    if (!tpl) return;
    var tierId = currentTierId(customer);
    var rules = (program.rules || []).filter(function (r) {
      return r.enabled && !r.deactivated && !r.deleted;
    }).sort(function (a, b) { return (a.sortKey || 0) - (b.sortKey || 0); });

    rules.forEach(function (rule) {
      var node = tpl.make();
      node.setAttribute('data-lion-action', RULE_SLUG[rule.kind] || rule.kind);
      setSrc(node, 'icon', RULE_ICON[rule.kind] || IMG + 'purchase.svg');
      setText(node, 'name', rule.title);
      var val = ruleValue(rule, tierId);
      setText(node, 'points', rule.kind === 'purchase' ? num(val) + ' points per $1' : num(val) + ' points');

      var btn = field(node, 'btn');
      if (btn) {
        var cfg = earnButton(rule, customer);
        if (!cfg.show) {
          btn.parentNode.removeChild(btn);
          node.classList.add('earn-card--no-btn');
        } else if (cfg.claimed) {
          btn.classList.add('earn-card__btn--claimed');
          btn.removeAttribute('href');
          btn.setAttribute('aria-disabled', 'true');
          btn.innerHTML = CHECK_SM + ' ' + cfg.text;
        } else if (cfg.disabled) {
          btn.classList.add('earn-card__btn--countdown');
          btn.removeAttribute('href');
          btn.setAttribute('aria-disabled', 'true');
          btn.innerHTML = (cfg.clock ? CLOCK_SVG + ' ' : '') + cfg.text;
        } else if (cfg.refer) {
          btn.textContent = cfg.text;
          btn.setAttribute('href', '#');
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            getModal().open({
              variant: 'centered',
              title: 'Refer your friends',
              bodyHtml: buildReferralBody(customer, program, val),
              onOpen: function (dialog) { wireReferral(dialog); }
            });
          });
        } else if (cfg.activity) {
          // Open the social page in a new tab AND track the activity to award points.
          btn.textContent = cfg.text;
          btn.setAttribute('href', cfg.href || '#');
          btn.setAttribute('target', '_blank');
          btn.setAttribute('rel', 'noopener');
          (function (name, url) {
            var pending = false;
            btn.addEventListener('click', function () {
              if (pending) return; // let the browser open the tab regardless
              pending = true;
              trackActivity(name, url, function () {
                try { renderHero(STATE.customer); } catch (e2) {}
                try { renderEarn(STATE.customer, STATE.program); } catch (e2) {}
              }, function () { pending = false; });
            });
          })(cfg.activityName, cfg.href);
        } else if (cfg.newsletter) {
          btn.textContent = cfg.text;
          btn.setAttribute('href', '#');
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            if (btn.getAttribute('aria-disabled') === 'true') return;
            btn.setAttribute('aria-disabled', 'true');
            btn.textContent = 'SIGNING UP…';
            btn.style.opacity = '0.7';
            signupNewsletter(function () {
              try { renderHero(STATE.customer); } catch (e2) {}
              try { renderEarn(STATE.customer, STATE.program); } catch (e2) {}
            }, function () {
              btn.removeAttribute('aria-disabled');
              btn.textContent = cfg.text;
              btn.style.opacity = '';
              alert('Sorry, we could not sign you up right now. Please try again.');
            });
          });
        } else if (cfg.birthday) {
          btn.textContent = cfg.text;
          btn.setAttribute('href', '#');
          (function (points) {
            btn.addEventListener('click', function (e) {
              e.preventDefault();
              openBirthdayModal(points);
            });
          })(val);
        } else if (cfg.modal) {
          btn.textContent = cfg.text;
          btn.setAttribute('href', '#');
          var body = rule.kind === 'purchase'
            ? 'Get ' + num(val) + ' points for every $1 you spend in our store'
            : (rule.description || rule.title);
          (function (title, b) {
            btn.addEventListener('click', function (e) {
              e.preventDefault();
              getModal().open({ variant: 'header', title: title, bodyHtml: '<p>' + b + '</p>' });
            });
          })(rule.title, body);
        } else {
          btn.textContent = cfg.text;
          btn.setAttribute('href', cfg.href || '#');
          if (cfg.external) { btn.setAttribute('target', '_blank'); btn.setAttribute('rel', 'noopener'); }
        }
      }
      tpl.grid.appendChild(node);
    });

    // Progressive reveal: show the first 4 cards, hide the rest behind a toggle.
    var cards = Array.prototype.slice.call(tpl.grid.querySelectorAll('.earn-card:not([data-ll-tpl])'));
    var toggle = ROOT.querySelector('[data-ll-earn-toggle]');
    if (toggle) {
      if (cards.length > 4) {
        cards.forEach(function (c, i) { if (i >= 4) c.style.display = 'none'; });
        toggle.textContent = 'Show all ' + cards.length + ' ways to earn';
        toggle.removeAttribute('hidden');
        toggle.onclick = function () {
          cards.forEach(function (c) { c.style.display = ''; });
          toggle.setAttribute('hidden', '');
        };
      } else {
        toggle.setAttribute('hidden', '');
      }
    }
    if (rules.length) showSection('earn');
  }

  /* ----- Merged Redeem (coupons + products) with search / sort / filters ----- */

  // The redeem grid carries two prototypes (product + coupon). Clear old clones,
  // return a factory for each kept prototype.
  function redeemProtos() {
    var grid = ROOT.querySelector('[data-ll-grid="redeem"]');
    if (!grid) return null;
    var pProd = grid.querySelector('[data-ll-tpl="product"]');
    var pCoup = grid.querySelector('[data-ll-tpl="coupon"]');
    if (!pProd || !pCoup) return null;
    Array.prototype.slice.call(grid.children).forEach(function (ch) {
      if (!ch.hasAttribute('data-ll-tpl')) grid.removeChild(ch);
    });
    var prod = pProd.cloneNode(true); prod.removeAttribute('data-ll-tpl'); prod.removeAttribute('hidden');
    var coup = pCoup.cloneNode(true); coup.removeAttribute('data-ll-tpl'); coup.removeAttribute('hidden');
    return {
      grid: grid,
      product: function () { return prod.cloneNode(true); },
      coupon: function () { return coup.cloneNode(true); }
    };
  }

  // Category is driven by the product's `custom.product_type_for_reward_filter`
  // metafield (choice list: Apparel, Bags, Accessories, Perks). Shopify's
  // /products/{handle}.js AJAX endpoint does not expose metafields, so
  // classifyCard reads the value from the product.reward-filter alternate template
  // (see fetchRewardFilter).
  //
  // Slug used on each card's data-cat: the metafield value, lowercased with spaces
  // collapsed to hyphens (e.g. "Gift Cards" -> "gift-cards").
  //
  // Only Apparel / Bags / Accessories have their own chip. Everything else lands in
  // the catch-all "Discounts & Perks" chip (marked data-cat-rest in
  // loyalty-lion-section.liquid): LoyaltyLion vouchers ($ off, % off, free shipping)
  // and manual/custom rewards, which are not Shopify products and so can never carry
  // a metafield; products tagged Perks; products whose metafield is unset; and any
  // new choice-list value added in Shopify later. See catMatches — nothing goes
  // unreachable just because the theme hasn't been updated.
  function catSlug(value) {
    var v = String(value || '').trim().toLowerCase();
    return v ? v.replace(/\s+/g, '-') : null;
  }
  // Initial (sync) category before the product's metafield has loaded. Coupon /
  // voucher and manual rewards have no backing product, so they fall under
  // Discounts & Perks for good.
  function initialCategory(reward) {
    return reward.kind !== 'product_cart' ? 'discounts' : 'other';
  }
  // The circle text on a coupon card.
  function couponParts(reward) {
    if (reward.kind === 'free_shipping_voucher') return { val: 'Free', off: 'shipping' };
    if (reward.kind === 'cart_discount_voucher' && reward.discount_type === 'flat') return { val: '$' + reward.discount_amount, off: 'Off' };
    if (reward.kind === 'cart_discount_voucher' && reward.discount_type === 'percentage') return { val: reward.discount_amount + '%', off: 'Off' };
    return { val: reward.title || 'Perk', off: '' };
  }

  /* ----- Product image slider (lazy gallery fetch by handle) ----- */

  // Cache of in-flight / resolved product data ({ urls, tags, type }), keyed by
  // handle, so a handle is fetched at most once even if it appears on several
  // cards and is shared by the gallery slider and the category classifier.
  var PRODUCT_CACHE = {};

  // Fetch a product's JSON once and resolve { urls, tags, type, avail }.
  // `avail` maps variant id (as a string) -> Shopify's `available` flag, so the
  // redeem popup can cross out sold-out sizes. `available` already folds in
  // inventory_policy (false only when tracked stock <= 0 AND policy is 'deny').
  function fetchProduct(handle) {
    if (!handle) return Promise.resolve({ urls: [], tags: [], type: '', avail: {}, price: 0 });
    if (!PRODUCT_CACHE[handle]) {
      PRODUCT_CACHE[handle] = fetchWithTimeout('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p) return { urls: [], tags: [], type: '', avail: {}, price: 0 };
          var raw = (p.images && p.images.length)
            ? p.images
            : (p.media || []).filter(function (m) { return m.media_type === 'image' && m.src; }).map(function (m) { return m.src; });
          var seen = {}, urls = [];
          raw.forEach(function (u) { if (u && !seen[u]) { seen[u] = 1; urls.push(u); } });
          var avail = {};
          (p.variants || []).forEach(function (v) { avail[String(v.id)] = v.available; });
          return { urls: urls.slice(0, 6), tags: p.tags || [], type: p.type || '', avail: avail, price: p.price || 0 };
        })
        .catch(function () { return { urls: [], tags: [], type: '', avail: {}, price: 0 }; });
    }
    return PRODUCT_CACHE[handle];
  }

  // Cache of reward-filter metafield values, keyed by handle. Read from the tiny
  // product.reward-filter alternate template because the .js AJAX endpoint above
  // does not expose metafields. Resolves to the trimmed choice-list value, '' when
  // the metafield is unset, or null when the fetch itself failed. '' and null must
  // stay distinct: '' means Discounts & Perks, while a network blip must not
  // silently drop an Apparel product into that bucket.
  var REWARD_FILTER_CACHE = {};
  function fetchRewardFilter(handle) {
    if (!handle) return Promise.resolve(null);
    if (!REWARD_FILTER_CACHE[handle]) {
      REWARD_FILTER_CACHE[handle] = fetchWithTimeout('/products/' + encodeURIComponent(handle) + '?view=reward-filter', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (t) { return t == null ? null : t.trim(); })
        .catch(function () { return null; });
    }
    return REWARD_FILTER_CACHE[handle];
  }

  // Run async jobs with limited concurrency so classifying every product reward
  // doesn't fire dozens of simultaneous product fetches at once.
  function makeThrottle(limit) {
    var active = 0, queue = [];
    function pump() {
      while (active < limit && queue.length) {
        active++;
        queue.shift()().then(step, step);
      }
    }
    function step() { active--; pump(); }
    return function (job) { queue.push(job); pump(); };
  }
  var classifyQueue = makeThrottle(6);

  // Debounced re-run of the redeem filters, so an active category filter updates
  // as product tags arrive and cards get their real data-cat.
  var reapplyTimer = null;
  function reapplyRedeemSoon() {
    if (reapplyTimer) return;
    reapplyTimer = setTimeout(function () { reapplyTimer = null; applyRedeemFilters(); }, 120);
  }

  // Fetch a product reward's reward-filter metafield (throttled) and stamp the
  // category slug used by the chips. An unset metafield ('') falls into the
  // Discounts & Perks catch-all; a failed fetch (null) leaves the card
  // uncategorized rather than guessing.
  function classifyCard(card, handle) {
    classifyQueue(function () {
      return fetchRewardFilter(handle).then(function (value) {
        if (value == null) return;
        card.setAttribute('data-cat', catSlug(value) || 'discounts');
        reapplyRedeemSoon();
      });
    });
  }
  // Camera icon for the photo-count badge.
  var PHOTO_ICON = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 8a2 2 0 0 1 2-2h2l1.2-1.6A1 1 0 0 1 9 4h6a1 1 0 0 1 .8.4L17 6h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8Z" stroke="currentColor" stroke-width="1.6"/><circle cx="12" cy="12.5" r="3.2" stroke="currentColor" stroke-width="1.6"/></svg>';

  // One shared observer: fetch a product's gallery only when its card scrolls
  // into view. Falls back to eager load when IntersectionObserver is missing.
  var GALLERY_OBS = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          var el = en.target;
          GALLERY_OBS.unobserve(el);
          loadGallery(el, el.getAttribute('data-ll-handle'), el.getAttribute('data-ll-title') || '');
        });
      }, { rootMargin: '200px 0px' })
    : null;

  // Register a product card's image area for a lazy gallery fetch.
  function observeGallery(imgEl, handle, title) {
  if (!imgEl || !handle) return;
  imgEl.setAttribute('data-ll-handle', handle);
  imgEl.setAttribute('data-ll-title', title || '');
 if (GALLERY_OBS) GALLERY_OBS.observe(imgEl);
    else loadGallery(imgEl, handle, title);
  }
  // Pull the product's image list from the Shopify product JSON, then build a
  // slider when there are 2+ images. Single image / errors keep the existing
  // single <img> already painted on the card.
  function loadGallery(imgEl, handle, title) {
    if (!imgEl || !handle || imgEl.getAttribute('data-ll-gallery') === '1') return;
    imgEl.setAttribute('data-ll-gallery', '1');
    fetchProduct(handle).then(function (data) {
      if (data.urls && data.urls.length >= 1) buildSlider(imgEl, data.urls, title);
    });
  }

  // Replace the card's single image with a swipeable slider (dots + hover arrows).
function buildSlider(imgEl, urls, title, pts) {
  var main = imgEl.querySelector('.r-card__img-main');
  if (!main) return;
  var alt = title || '';

  /* ── inline carousel (card thumbnail) ── */
  var track = document.createElement('div');
  track.className = 'r-card__slider';
  urls.forEach(function (u, i) {
    var img = document.createElement('img');
    if (i === 0) img.setAttribute('src', u); else img.setAttribute('data-src', u);
    img.setAttribute('alt', alt);
    img.setAttribute('loading', 'lazy');
    track.appendChild(img);
  });
  main.innerHTML = '';
  main.appendChild(track);

  var dots = document.createElement('div');
  dots.className = 'r-card__dots';
  urls.forEach(function (_, i) {
    var d = document.createElement('span');
    d.className = 'r-card__dot' + (i === 0 ? ' is-active' : '');
    d.addEventListener('click', function (e) { e.stopPropagation(); e.preventDefault(); go(i); });
    dots.appendChild(d);
  });

  var prev = document.createElement('button');
  prev.type = 'button';
  prev.className = 'r-card__nav r-card__nav--prev';
  prev.setAttribute('aria-label', 'Previous image');
  prev.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var next = document.createElement('button');
  next.type = 'button';
  next.className = 'r-card__nav r-card__nav--next';
  next.setAttribute('aria-label', 'Next image');
  next.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  prev.addEventListener('click', function (e) { e.stopPropagation(); e.preventDefault(); go(idx - 1); });
  next.addEventListener('click', function (e) { e.stopPropagation(); e.preventDefault(); go(idx + 1); });

  var tag = document.createElement('span');
  tag.className = 'r-card__photo-tag';
  tag.innerHTML = PHOTO_ICON + '<span>' + urls.length + '</span>';
  if (urls.length > 1) {
    imgEl.appendChild(prev);
    imgEl.appendChild(next);
  }
  imgEl.appendChild(tag);

  var slides = track.children, dotEls = dots.children, idx = 0;
  prev.disabled = true;
  next.disabled = urls.length <= 1;

  function loadSlide(n) {
    var s = slides[n];
    if (s && !s.getAttribute('src')) s.setAttribute('src', s.getAttribute('data-src') || '');
  }

  function go(i) {
    // Clamp instead of wrapping — reaching the last image and pressing next
    // must not trigger a full rewind animation back to the first slide.
    if (i < 0 || i >= urls.length) return;
    idx = i;
    loadSlide(idx);
    // Preload neighbours so sliding never reveals a blank/coloured gap.
    loadSlide(idx - 1);
    loadSlide(idx + 1);
    track.style.transform = 'translateX(' + (-idx * 100) + '%)';
    for (var d = 0; d < dotEls.length; d++) dotEls[d].classList.toggle('is-active', d === idx);
    prev.disabled = idx === 0;
    next.disabled = idx === urls.length - 1;
  }

  /* ── swipe on card ── */
  var startX = null, dragging = false;
  track.addEventListener('pointerdown', function (e) { startX = e.clientX; dragging = true; });
  track.addEventListener('pointermove', function (e) {
    if (!dragging || startX === null) return;
    // No in-card slider: only allow swipe navigation while in the click popup.
    if (!track.closest('.r-popup__content')) return;
    var dx = e.clientX - startX;
    if (Math.abs(dx) > 40) { dragging = false; e.stopPropagation(); go(idx + (dx < 0 ? 1 : -1)); }
  });
  function endDrag() { dragging = false; startX = null; }
  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);
  track.addEventListener('pointerleave', endDrag);
}

  /* ══════════════════════════════════════════
     POPUP — the reward-preview overlay.

     This runs at module load. It used to sit INSIDE buildSlider, so the
     overlay (and _openProductPreview, which it assigns) only came into
     existence once some card's gallery had lazily loaded. Until then a
     "View Product" / "Get Reward" click fell through to openRedeemConfirm,
     which has no image — which is why the Exclusive Shop preview opened
     without its photos.
  ══════════════════════════════════════════ */

function initCardPopups() {
    if (document.querySelector('.r-popup__overlay.lp-index')) return;

  if (document.querySelector('.r-popup__overlay')) return;
  var overlay = document.createElement('div');
  overlay.className = 'r-popup__overlay lp-index';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Reward preview');
  document.body.appendChild(overlay);

  var modal = document.createElement('div');
  modal.className = 'r-popup__modal';
  overlay.appendChild(modal);

  var closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'r-popup__close';
  closeBtn.setAttribute('aria-label', 'Close preview');
  closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
  closeBtn.addEventListener('click', closePopup);
  modal.appendChild(closeBtn);
document.addEventListener('click', function (e) {
  if (e.target.closest('.ll-modal__close') || e.target.closest('.ll-redeem__btn[data-close]')) {
    closePopup();
  }
});
  var popupContent = document.createElement('div');
  popupContent.className = 'r-popup__content';
  modal.appendChild(popupContent);

  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) closePopup();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay.classList.contains('is-open')) closePopup();
  });

function openPopup(card) {
  var img = card.querySelector('.r-card__img');
  var body = card.querySelector('.r-card__body');
  var btn = card.querySelector('.r-card__btn');

  overlay.classList.remove('r-popup--product');
  popupContent.innerHTML = '';

  if (img) {
    // Clone the image (don't move it) so the source card keeps its picture
    // while the popup is open. cloneNode drops the slider's event handlers,
    // so we re-wire navigation on the clone below.
    var imgClone = img.cloneNode(true);
    popupContent.appendChild(imgClone);
    initPopupSlider(imgClone);
  }
  if (body) {
    var bodyClone = body.cloneNode(true);
    // Terms and Conditions stay on the card only — not shown in the popup.
    var terms = bodyClone.querySelector('.r-card__terms');
    if (terms) terms.remove();
    popupContent.appendChild(bodyClone);
  }
  if (btn) {
    var clonedBtn = btn.cloneNode(true);
    clonedBtn.className = btn.className;
    // cloneNode drops the original click handler — forward to the real card
    // button so "Get Reward" (and the guest "Join to redeem" link) works.
    clonedBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (btn.getAttribute('aria-disabled') === 'true') return; // locked state
      closePopup();
      btn.click(); // runs the original handler (redeem confirm) or follows its href
    });
    popupContent.appendChild(clonedBtn);
  }

  overlay.classList.add('is-open');
  document.body.style.overflow = 'hidden';
  overlay.setAttribute('tabindex', '-1');
  overlay.focus();
}

// Combined product preview: image carousel + Cost/Balance-after header + inline
// size buttons + Not Now / Redeem. Replaces the old "preview then a separate
// size-selection modal" two-step for redeemable product rewards.
function openProductPreview(reward, cost, card) {
  overlay.classList.add('r-popup--product');
  popupContent.innerHTML = '';

  var head = document.createElement('div');
  head.className = 'r-popup__head';
  var balAfter = (STATE.customer && STATE.customer.pointsApproved != null)
    ? Math.max(0, STATE.customer.pointsApproved - cost) : null;
  head.innerHTML =
    '<h3 class="r-popup__title">' + escapeHtml(reward.title || 'Reward') + '</h3>' +
    '<p class="r-popup__meta">Cost: <strong>' + num(cost) + ' pts</strong>' +
      (balAfter != null ? '<span class="r-popup__meta-dot"></span>Balance after: <strong>' + num(balAfter) + ' pts</strong>' : '') +
    '</p>';
  popupContent.appendChild(head);

  // Clone the card's image (keeps the source card intact); drop the points
  // badge since the cost now lives in the header, then re-wire the slider.
  var img = card && card.querySelector('.r-card__img');
  if (img) {
    var imgClone = img.cloneNode(true);
    var tag = imgClone.querySelector('.r-card__pts-tag');
    if (tag) tag.remove();
    popupContent.appendChild(imgClone);
    initPopupSlider(imgClone);
  }

  // Size buttons — only when the product actually has multiple variants.
  var variants = reward.target_products || [];
  var sizeWrap = null;
  if (variants.length > 1) {
    sizeWrap = document.createElement('div');
    sizeWrap.className = 'r-popup__sizes';
    var btnsHtml = variants.map(function (v, i) {
      return '<button type="button" class="r-popup__size' + (i === 0 ? ' is-active' : '') + '"' +
        ' data-variant="' + escapeHtml(v.variant_id) + '" data-product="' + escapeHtml(v.id) + '">' +
        escapeHtml(v.title || '') + '</button>';
    }).join('');
    sizeWrap.innerHTML = '<span class="r-popup__sizes-label">Size</span>' +
      '<div class="r-popup__size-grid">' + btnsHtml + '</div>';
    popupContent.appendChild(sizeWrap);
    sizeWrap.addEventListener('click', function (e) {
      var b = e.target.closest('.r-popup__size');
      if (!b || b.classList.contains('r-popup__size--soldout')) return;
      var all = sizeWrap.querySelectorAll('.r-popup__size');
      for (var k = 0; k < all.length; k++) all[k].classList.remove('is-active');
      b.classList.add('is-active');
    });
  }

  var actions = document.createElement('div');
  actions.className = 'r-popup__actions';
  actions.innerHTML =
    '<button type="button" class="ll-claim__btn ll-claim__btn--ghost" data-rp-close>Not Now</button>' +
    '<button type="button" class="ll-claim__btn ll-claim__btn--primary" data-redeem>Redeem</button>';
  popupContent.appendChild(actions);

  actions.querySelector('[data-rp-close]').addEventListener('click', closePopup);
  var redeemBtn = actions.querySelector('[data-redeem]');

  // Cross out sizes that are sold out. Availability comes from the product's
  // /products/{handle}.js JSON (same cached/throttled fetch the cards use); all
  // sizes share one product, so variants[0].handle is the product handle. Fail
  // open: an unknown/missing variant stays selectable so we never wrongly block
  // a redeemable size.
  if (sizeWrap && variants[0] && variants[0].handle) {
    fetchProduct(variants[0].handle).then(function (data) {
      var avail = (data && data.avail) || {};
      var btns = sizeWrap.querySelectorAll('.r-popup__size');
      var firstAvailable = null, allSoldOut = true;
      for (var k = 0; k < btns.length; k++) {
        var b = btns[k];
        if (avail[String(b.getAttribute('data-variant'))] === false) {
          b.classList.add('r-popup__size--soldout');
          b.classList.remove('is-active');
          b.disabled = true;
          b.setAttribute('aria-disabled', 'true');
        } else {
          allSoldOut = false;
          if (!firstAvailable) firstAvailable = b;
        }
      }
      // Default selection may have landed on a sold-out size — move it to the
      // first in-stock one, or disable Redeem entirely if nothing is in stock.
      if (!sizeWrap.querySelector('.r-popup__size.is-active') && firstAvailable) {
        firstAvailable.classList.add('is-active');
      }
      if (allSoldOut) {
        redeemBtn.disabled = true;
        redeemBtn.setAttribute('aria-disabled', 'true');
        redeemBtn.textContent = 'Sold Out';
      }
    });
  }

  redeemBtn.addEventListener('click', function () {
    var sel = sizeWrap ? sizeWrap.querySelector('.r-popup__size.is-active') : null;
    var variantId, productId;
    if (sel) {
      variantId = sel.getAttribute('data-variant');
      productId = sel.getAttribute('data-product');
    } else {
      // No explicit selection: pick the first in-stock variant (index 0 may be
      // sold out), falling back to variants[0] if availability is unknown.
      var pick = variants.filter(function (v) {
        var b = sizeWrap && sizeWrap.querySelector('.r-popup__size[data-variant="' + v.variant_id + '"]');
        return !b || !b.classList.contains('r-popup__size--soldout');
      })[0] || variants[0] || {};
      variantId = pick.variant_id;
      productId = pick.id;
    }
    // redeemProduct handles the REDEEMING… state and, on success, closes this
    // preview (closeRewardPreview) and shows the "Reward Claimed!" popup.
    redeemProduct(reward, productId, variantId, redeemBtn);
  });

  overlay.classList.add('is-open');
  document.body.style.overflow = 'hidden';
  overlay.setAttribute('tabindex', '-1');
  overlay.focus();
}
_openProductPreview = openProductPreview;

// Re-wire the carousel on the cloned image inside the popup: preload every
// slide (no blank/green flash), clamp navigation (no end-of-gallery rewind),
// and disable the arrows at the first/last slide.
function initPopupSlider(root) {
  var track = root.querySelector('.r-card__slider');
  if (!track) return; // single-image reward, nothing to wire
  var slides = track.querySelectorAll('img');
  if (slides.length < 2) return;

  for (var i = 0; i < slides.length; i++) {
    if (!slides[i].getAttribute('src')) {
      slides[i].setAttribute('src', slides[i].getAttribute('data-src') || '');
    }
  }

  var prev = root.querySelector('.r-card__nav--prev');
  var next = root.querySelector('.r-card__nav--next');
  var idx = 0;

  function render() {
    track.style.transform = 'translateX(' + (-idx * 100) + '%)';
    if (prev) prev.disabled = idx === 0;
    if (next) next.disabled = idx === slides.length - 1;
  }
  function go(i) { if (i < 0 || i >= slides.length) return; idx = i; render(); }

  if (prev) prev.addEventListener('click', function (e) { e.stopPropagation(); e.preventDefault(); go(idx - 1); });
  if (next) next.addEventListener('click', function (e) { e.stopPropagation(); e.preventDefault(); go(idx + 1); });

  var startX = null, dragging = false;
  track.addEventListener('pointerdown', function (e) { startX = e.clientX; dragging = true; });
  track.addEventListener('pointermove', function (e) {
    if (!dragging || startX === null) return;
    var dx = e.clientX - startX;
    if (Math.abs(dx) > 40) { dragging = false; e.stopPropagation(); go(idx + (dx < 0 ? 1 : -1)); }
  });
  function endDrag() { dragging = false; startX = null; }
  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);
  track.addEventListener('pointerleave', endDrag);

  render();
}

function closePopup() {
  overlay.classList.remove('is-open');
  document.body.style.overflow = '';
}

  // only attachGrid + MutationObserver below, nothing else
  function attachGrid() {
    var grid = document.querySelector('[data-ll-grid="redeem"]');
    if (!grid) return false;
    var firstCard = grid.querySelector('.r-card:not([hidden])');
    if (!firstCard) return false;

    grid.addEventListener('click', function (e) {
      // The button handles its own action (guest -> login/account page,
      // member -> Get Reward). Clicking the image/body opens the preview popup.
      if (e.target.closest('.r-card__btn')) return;
      var card = e.target.closest('.r-card');
      if (!card || card.hasAttribute('hidden')) return;
      // Redeemable product cards carry the reward object (set in renderRedeemMerged)
      // and open the combined preview + size + redeem popup; everything else keeps
      // the plain image/body preview.
      if (card.__llReward) { openProductPreview(card.__llReward, card.__llCost, card); return; }
      openPopup(card);
    });

    return true;
  }

  if (!attachGrid()) {
    var obs = new MutationObserver(function () {
      if (attachGrid()) obs.disconnect();
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }
}
initCardPopups();

  function renderRedeemMerged(customer, program, tierId) {
    var protos = redeemProtos();
    if (!protos) return;
    // Drop product rewards with nothing to give, and anything the customer's tier
    // does not offer — a tier-locked reward is hidden outright rather than shown
    // as unobtainable.
    var rewards = (program.rewards || []).filter(function (r) {
      return (r.kind !== 'product_cart' || (r.target_products || []).length) &&
        rewardEnabledForTier(r, program, tierId);
    });
    var sym = STATE.sym || '$';

    rewards.forEach(function (reward, i) {
      var isProduct = reward.kind === 'product_cart';
      var cost = rewardCost(reward, program, tierId);
      var affordable = !!(customer && customer.pointsApproved >= cost);
      var node = isProduct ? protos.product() : protos.coupon();

      // filter metadata
      node.setAttribute('data-cost', cost);
      node.setAttribute('data-cat', initialCategory(reward));
      node.setAttribute('data-name', (reward.title || '').toLowerCase());
      node.setAttribute('data-affordable', affordable ? '1' : '0');
      node.setAttribute('data-order', i); // original LoyaltyLion order

      setText(node, 'name', reward.title || (isProduct ? 'Product' : 'Reward'));
      setText(node, 'pts', num(cost) + ' pts');

      if (isProduct) {
        var product = reward.target_products[0] || {};
        // Classify from the product's reward-filter metafield (throttled fetch).
        if (product.handle) classifyCard(node, product.handle);
        var imgWrap = node.querySelector('.r-card__img-main');
        var img = node.querySelector('[data-ll-field="image"]');
        var url = product.image_url;
        // No real product image -> show a clean single-letter tile instead of
        // a broken image / the full title text.
        var showInitial = function () {
          if (!imgWrap) return;
          var initial = ((reward.title || '?').trim().charAt(0) || '?').toUpperCase();
          imgWrap.innerHTML = '<div class="r-card__img-fallback"><span>' + escapeHtml(initial) + '</span></div>';
        };
        if (url && img) {
          img.setAttribute('src', url);
          img.setAttribute('alt', reward.title || '');
          img.addEventListener('error', showInitial);
          // Lazily fetch the product's full gallery (by handle) and, when it has
          // 2+ images, upgrade this card's single image into a slider.
          var imgEl = node.querySelector('.r-card__img');
          if (imgEl && product.handle) observeGallery(imgEl, product.handle, reward.title || '');
        } else {
          showInitial();
        }
        setText(node, 'ptsTag', num(cost) + ' pts');
      } else {
        var cp = couponParts(reward);
        setText(node, 'couponVal', cp.val);
        setText(node, 'couponOff', cp.off);
      }

      // Terms (reward description) link in the body.
      var terms = (reward.description || '').trim();
      if (terms) {
        var body = node.querySelector('.r-card__body');
        if (body) {
          var tc = document.createElement('span');
          tc.className = 'r-card__terms';
          tc.textContent = 'Terms and Conditions';
          tc.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); openTerms(reward); });
          body.appendChild(tc);
        }
      }

      // Button state: guest -> Join, unaffordable -> locked, product min-spend gating, else redeem.
      var btn = field(node, 'btn');
      if (btn) {
        if (!customer) {
          btn.textContent = 'Join to redeem';
          btn.setAttribute('href', '/account/login');
        } else if (!affordable) {
          btn.textContent = 'More points needed';
          btn.classList.add('r-card__btn--locked');
          btn.setAttribute('aria-disabled', 'true');
          btn.removeAttribute('href');
        } else if (isProduct) {
          var minReq = reward.minimum_requirements;
          var minSpendCents = (minReq && minReq.type === 'cost' && minReq.cost) ? minReq.cost * 100 : 0;
          var remainingCents = Math.max(0, minSpendCents - (STATE.cartSubtotal || 0));
          if (remainingCents > 0) {
            btn.textContent = 'Spend ' + moneyDual(remainingCents, sym) + ' more';
            btn.classList.add('r-card__btn--locked');
            btn.setAttribute('aria-disabled', 'true');
            btn.removeAttribute('href');
          } else {
            // Redeemable product: open the combined preview (image + size buttons
            // + redeem) directly — no separate size-selection modal.
            node.__llReward = reward;
            node.__llCost = cost;
            btn.textContent = 'Get Reward';
            btn.setAttribute('href', '#');
            btn.addEventListener('click', function (e) {
              e.preventDefault();
              if (_openProductPreview) _openProductPreview(reward, cost, node);
              else openRedeemConfirm(reward, cost);
            });
          }
        } else {
          btn.textContent = 'Get Reward';
          btn.setAttribute('href', '#');
          btn.addEventListener('click', function (e) { e.preventDefault(); openRedeemConfirm(reward, cost); });
        }
      }
      protos.grid.appendChild(node);
    });

    if (rewards.length) showSection('redeem');
    applyRedeemFilters();
  }

  // Active filter/sort state for the redeem grid. `catRest` is set when the active
  // category chip is the catch-all one (Discounts & Perks) rather than a specific slug.
  var REDEEM_FILTERS = { search: '', sort: 'featured', afford: false, ptsMin: null, ptsMax: null, cat: null, catRest: false };

  // Slugs owned by the explicit category chips (Apparel / Bags / Accessories), read
  // straight off the markup so adding a chip in loyalty-lion-section.liquid narrows the
  // catch-all automatically — there is no second list to keep in sync.
  var EXPLICIT_CATS = null;
  function explicitCats() {
    if (!EXPLICIT_CATS) {
      EXPLICIT_CATS = Array.prototype.slice.call(ROOT.querySelectorAll('[data-ll-filter="cat"]'))
        .filter(function (b) { return !b.hasAttribute('data-cat-rest'); })
        .map(function (b) { return b.getAttribute('data-cat'); });
    }
    return EXPLICIT_CATS;
  }

  // Does a card's data-cat satisfy the active category filter?
  // The catch-all chip takes everything that resolved to something no explicit chip
  // claims. 'other' is excluded on purpose: it means the metafield fetch failed or
  // hasn't landed yet, and guessing would drop an Apparel product into the wrong pill.
  function catMatches(cat, f) {
    if (!f.catRest) return cat === f.cat;
    if (!cat || cat === 'other') return false;
    return explicitCats().indexOf(cat) === -1;
  }

  function applyRedeemFilters() {
    var grid = ROOT.querySelector('[data-ll-grid="redeem"]');
    if (!grid) return;
    var cards = Array.prototype.slice.call(grid.querySelectorAll('.r-card:not([data-ll-tpl])'));
    var f = REDEEM_FILTERS, shown = 0;
    cards.forEach(function (c) {
      var cost = parseInt(c.getAttribute('data-cost'), 10) || 0;
      var name = c.getAttribute('data-name') || '';
      var cat = c.getAttribute('data-cat') || '';
      var aff = c.getAttribute('data-affordable') === '1';
      var ok = true;
      if (f.search && name.indexOf(f.search) === -1) ok = false;
      if (f.afford && !aff) ok = false;
      if (f.ptsMin != null && (cost < f.ptsMin || cost > f.ptsMax)) ok = false;
      if (f.cat && !catMatches(cat, f)) ok = false;
      c.style.display = ok ? '' : 'none';
      if (ok) shown++;
    });
    // Re-order the visible cards.
    var visible = cards.filter(function (c) { return c.style.display !== 'none'; });
    visible.sort(function (a, b) {
      var ca = parseInt(a.getAttribute('data-cost'), 10) || 0;
      var cb = parseInt(b.getAttribute('data-cost'), 10) || 0;
      if (f.sort === 'low') return ca - cb;
      if (f.sort === 'high') return cb - ca;
      // 'featured' (default): preserve LoyaltyLion's native reward order.
      var oa = parseInt(a.getAttribute('data-order'), 10) || 0;
      var ob = parseInt(b.getAttribute('data-order'), 10) || 0;
      return oa - ob;
    });
    visible.forEach(function (c) { grid.appendChild(c); });
    var count = ROOT.querySelector('[data-ll-redeem-count]');
    if (count) count.textContent = 'Showing ' + shown + (shown === 1 ? ' reward' : ' rewards');
  }

  // Wire the static search/sort/filter controls once.
  function wireRedeemControls() {
    var search = ROOT.querySelector('[data-ll-redeem-search]');
    if (search) search.addEventListener('input', function () {
      REDEEM_FILTERS.search = this.value.trim().toLowerCase();
      applyRedeemFilters();
    });
    var sort = ROOT.querySelector('[data-ll-redeem-sort]');
    if (sort) sort.addEventListener('change', function () {
      REDEEM_FILTERS.sort = this.value;
      applyRedeemFilters();
    });
    Array.prototype.forEach.call(ROOT.querySelectorAll('[data-ll-filter]'), function (btn) {
      btn.addEventListener('click', function () {
        var kind = btn.getAttribute('data-ll-filter');
        if (kind === 'afford') {
          REDEEM_FILTERS.afford = !REDEEM_FILTERS.afford;
          btn.classList.toggle('active', REDEEM_FILTERS.afford);
        } else if (kind === 'pts') {
          var wasActive = btn.classList.contains('active');
          Array.prototype.forEach.call(ROOT.querySelectorAll('[data-ll-filter="pts"]'), function (b) { b.classList.remove('active'); });
          if (wasActive) { REDEEM_FILTERS.ptsMin = null; REDEEM_FILTERS.ptsMax = null; }
          else {
            btn.classList.add('active');
            REDEEM_FILTERS.ptsMin = parseInt(btn.getAttribute('data-min'), 10);
            REDEEM_FILTERS.ptsMax = parseInt(btn.getAttribute('data-max'), 10);
          }
        } else if (kind === 'cat') {
          var was = btn.classList.contains('active');
          Array.prototype.forEach.call(ROOT.querySelectorAll('[data-ll-filter="cat"]'), function (b) { b.classList.remove('active'); });
          REDEEM_FILTERS.cat = was ? null : btn.getAttribute('data-cat');
          REDEEM_FILTERS.catRest = !was && btn.hasAttribute('data-cat-rest');
          if (!was) btn.classList.add('active');
        }
        applyRedeemFilters();
        // Drop focus so a tapped button doesn't retain a highlighted state.
        try { btn.blur(); } catch (e) {}
      });
    });
    var toggle = ROOT.querySelector('[data-ll-filter-toggle]');
    var bar = ROOT.querySelector('[data-ll-filter-bar]');
    if (toggle && bar) toggle.addEventListener('click', function () { bar.classList.toggle('is-open'); });
  }


  /* =========================================================================
     EXCLUSIVE SHOP — members-only product marketplace
     The same LoyaltyLion product rewards the Redeem grid uses, minus the
     coupons/vouchers, presented as a shop with its own search, sort and
     paging. Namespaced XM / applyExclusive so nothing here touches
     REDEEM_FILTERS or applyRedeemFilters.
     ========================================================================= */

  var XM = { search: '', sort: 'afford', page: 1, perPage: 12 };
  // Price + photo count come from /products/{handle}.js. Throttled, and only
  // requested for cards on the current page, so a large catalogue doesn't fire
  // dozens of fetches on first paint.
  var xmMetaQueue = makeThrottle(4);

  function exclusiveProto() {
    var grid = ROOT.querySelector('[data-ll-grid="exclusive"]');
    if (!grid) return null;
    var tpl = grid.querySelector('[data-ll-tpl]');
    if (!tpl) return null;
    Array.prototype.slice.call(grid.children).forEach(function (ch) {
      if (!ch.hasAttribute('data-ll-tpl')) grid.removeChild(ch);
    });
    var base = tpl.cloneNode(true);
    base.removeAttribute('data-ll-tpl');
    base.removeAttribute('hidden');
    return { grid: grid, make: function () { return base.cloneNode(true); } };
  }

  // Fill in a card's price. Runs at most once per card. (The photo-count badge
  // and slider come from buildSlider via observeGallery, same as redeem.)
  function loadExclusivePrice(node) {
    if (node.__llPriceDone) return;
    var handle = node.getAttribute('data-handle');
    if (!handle) return;
    node.__llPriceDone = true;
    xmMetaQueue(function () {
      return fetchProduct(handle).then(function (data) {
        var priceEl = field(node, 'price');
        if (priceEl && data.price) {
          priceEl.textContent = money(data.price, STATE.sym || '$');
          priceEl.hidden = false;
        }
      });
    });
  }

  function renderExclusive(customer, program, tierId) {
    var protos = exclusiveProto();
    if (!protos) return;
    var sym = STATE.sym || '$';
    // Products only — a coupon has no photo, price or product page, so it has
    // nothing to show in a marketplace card.
    var rewards = (program.rewards || []).filter(function (r) {
      return r.kind === 'product_cart' &&
        (r.target_products || []).length &&
        rewardEnabledForTier(r, program, tierId);
    });
    if (!rewards.length) { hideSection('exclusive'); return; }

    rewards.forEach(function (reward, i) {
      var cost = rewardCost(reward, program, tierId);
      var affordable = !!(customer && customer.pointsApproved >= cost);
      var product = reward.target_products[0] || {};
      var node = protos.make();

      node.setAttribute('data-cost', cost);
      node.setAttribute('data-name', (reward.title || '').toLowerCase());
      node.setAttribute('data-affordable', affordable ? '1' : '0');
      node.setAttribute('data-order', i); // original LoyaltyLion order
      if (product.handle) node.setAttribute('data-handle', product.handle);

      setText(node, 'name', reward.title || 'Product');
      setText(node, 'pts', num(cost) + ' pts');

      var imgWrap = node.querySelector('.r-card__img-main');
      var img = field(node, 'image');
      // No usable product photo -> single-letter tile, the same fallback the
      // redeem cards use rather than a broken image.
      var showInitial = function () {
        if (!imgWrap) return;
        var initial = ((reward.title || '?').trim().charAt(0) || '?').toUpperCase();
        imgWrap.innerHTML = '<div class="r-card__img-fallback"><span>' + escapeHtml(initial) + '</span></div>';
      };
      if (product.image_url && img) {
        img.setAttribute('src', product.image_url);
        img.setAttribute('alt', reward.title || '');
        img.addEventListener('error', showInitial);
        // Lazily fetch the product's full gallery and upgrade the single image
        // into a slider + photo-count badge — the card shows the badge only
        // (arrows/dots are hidden in the grid), the popup gets the slider.
        var imgEl = node.querySelector('.r-card__img');
        if (imgEl && product.handle) observeGallery(imgEl, product.handle, reward.title || '');
      } else {
        showInitial();
      }

      // Button mirrors the redeem grid's gating, so an unaffordable or
      // spend-gated reward can't open a redeem flow that would fail.
      var btn = field(node, 'btn');
      if (btn) {
        if (!customer) {
          btn.textContent = 'Join to Redeem';
          btn.setAttribute('href', '/account/login');
        } else if (!affordable) {
          btn.textContent = 'More Points Needed';
          btn.classList.add('xm-card__btn--locked');
          btn.setAttribute('aria-disabled', 'true');
          btn.removeAttribute('href');
        } else {
          var minReq = reward.minimum_requirements;
          var minSpendCents = (minReq && minReq.type === 'cost' && minReq.cost) ? minReq.cost * 100 : 0;
          var remainingCents = Math.max(0, minSpendCents - (STATE.cartSubtotal || 0));
          if (remainingCents > 0) {
            btn.textContent = 'Spend ' + moneyDual(remainingCents, sym) + ' More';
            btn.classList.add('xm-card__btn--locked');
            btn.setAttribute('aria-disabled', 'true');
            btn.removeAttribute('href');
          } else {
            btn.textContent = 'View Product';
            btn.setAttribute('href', '#');
            btn.addEventListener('click', function (e) {
              e.preventDefault();
              if (_openProductPreview) _openProductPreview(reward, cost, node);
              else openRedeemConfirm(reward, cost);
            });
          }
        }
      }

      protos.grid.appendChild(node);
    });

    showSection('exclusive');
    applyExclusive();
  }

  function applyExclusive() {
    var grid = ROOT.querySelector('[data-ll-grid="exclusive"]');
    if (!grid) return;
    var cards = Array.prototype.slice.call(grid.querySelectorAll('.xm-card:not([data-ll-tpl])'));
    var q = XM.search;
    var matched = cards.filter(function (c) {
      return !q || (c.getAttribute('data-name') || '').indexOf(q) !== -1;
    });

    matched.sort(function (a, b) {
      var ca = parseInt(a.getAttribute('data-cost'), 10) || 0;
      var cb = parseInt(b.getAttribute('data-cost'), 10) || 0;
      if (XM.sort === 'low') return ca - cb;
      if (XM.sort === 'high') return cb - ca;
      // 'afford' (default): what this customer can redeem today first, cheapest
      // of those first, then everything else by cost. Guests have nothing
      // affordable, so for them this degrades to plain low-to-high.
      var aa = a.getAttribute('data-affordable') === '1';
      var ab = b.getAttribute('data-affordable') === '1';
      if (aa !== ab) return aa ? -1 : 1;
      return ca - cb;
    });

    cards.forEach(function (c) { c.style.display = 'none'; });
    var limit = XM.page * XM.perPage;
    matched.forEach(function (c, i) {
      grid.appendChild(c);
      if (i >= limit) return;
      c.style.display = '';
      loadExclusivePrice(c);
    });

    var shown = Math.min(limit, matched.length);
    var count = ROOT.querySelector('[data-ll-xm-count]');
    if (count) {
      count.textContent = shown < matched.length
        ? 'Showing ' + shown + ' of ' + matched.length + ' items'
        : 'Showing ' + shown + (shown === 1 ? ' item' : ' items');
    }
    var moreWrap = ROOT.querySelector('[data-ll-xm-more-wrap]');
    if (moreWrap) moreWrap.hidden = matched.length <= limit;
  }

  // Wire the static search / sort / load-more controls once.
  function wireExclusiveControls() {
    var search = ROOT.querySelector('[data-ll-xm-search]');
    if (search) search.addEventListener('input', function () {
      XM.search = this.value.trim().toLowerCase();
      XM.page = 1; // a new query starts from the first page again
      applyExclusive();
    });
    var sort = ROOT.querySelector('[data-ll-xm-sort]');
    if (sort) sort.addEventListener('change', function () {
      XM.sort = this.value;
      XM.page = 1;
      applyExclusive();
    });
    var more = ROOT.querySelector('[data-ll-xm-more]');
    if (more) more.addEventListener('click', function () {
      XM.page++;
      applyExclusive();
    });
  }

  function renderHistory(customer) {
    if (!customer || !(customer.actions && customer.actions.length)) {
      hideSection('history');
      return;
    }
    showSection('history');

    // Points-expiration status: alert the customer to the soonest batch of
    // points due to expire (LoyaltyLion `pointsExpiringNext` / `pointsExpiryDate`).
    var expSoon = ROOT.querySelector('[data-ll-expiry-soon]');
    if (expSoon) {
      var expPts = customer.pointsExpiringNext;
      var expWhen = fmtDateUTC(customer.pointsExpiryDate);
      if (expPts && expPts > 0 && expWhen) {
        var expPtsEl = ROOT.querySelector('[data-ll-expiry-points]');
        var expDateEl = ROOT.querySelector('[data-ll-expiry-date]');
        if (expPtsEl) expPtsEl.textContent = num(expPts);
        if (expDateEl) expDateEl.textContent = expWhen;
        expSoon.hidden = false;
      } else {
        expSoon.hidden = true;
      }
    }

    var actions = customer.actions.slice().sort(function (a, b) {
      return new Date(b.date) - new Date(a.date);
    });

    // Columns: Date / Activity / Order / Points (signed). One builder, two grids.
    function buildRow(tpl, a) {
      var node = tpl.make();
      setText(node, 'date', fmtDate(a.date));
      setText(node, 'activity', a.ruleTitle || a.rewardTitle || (a.note && a.note.en) || 'Activity');
      setText(node, 'order', a.orderName || a.order_name || '—');
      var signed = a.type === 'reward' ? -Math.abs(a.points) : a.points;
      var ptsEl = field(node, 'points');
      if (ptsEl) {
        ptsEl.textContent = (signed > 0 ? '+' : (signed < 0 ? '−' : '')) + num(Math.abs(signed));
        ptsEl.classList.add(signed < 0 ? 'pts-neg' : 'pts-pos');
      }
      return node;
    }

function setupGrid(gridName, moreName) {
  var tpl = gridTemplate(gridName);
  if (!tpl) return;
  var INITIAL = 2;
  var shown = 0;
  var moreBtn = ROOT.querySelector('[data-ll-more="' + moreName + '"]');
  function renderRows(count) {
    var next = actions.slice(shown, shown + count);
    next.forEach(function (a) { tpl.grid.appendChild(buildRow(tpl, a)); });
    shown += next.length;
    if (shown >= actions.length && moreBtn) moreBtn.style.display = 'none';
  }
  if (moreBtn) {
    moreBtn.style.display = '';
    moreBtn.onclick = function () { renderRows(actions.length - shown); }; // reveal the rest
  }
  renderRows(INITIAL);
}

    setupGrid('history', 'history');
    setupGrid('history-mobile', 'history-mobile');
  }

  /* =========================================================================
     BOOTSTRAP
     ========================================================================= */
  function ready(timeout) {
    return new Promise(function (resolve) {
      var t0 = Date.now();
      (function poll() {
        var L = window.loyaltylion;
        // Need the program catalogue (rules/rewards/tiers) before we can render.
        if (L && L.token && L.program && L.program.rewards) return resolve(L);
        if (Date.now() - t0 > timeout) return resolve(L && L.token ? L : null);
        setTimeout(poll, 150);
      })();
    });
  }

  function start() {
    ROOT = document.querySelector('.lp-index');
    if (!ROOT) return;
    var loader = ROOT.querySelector('[data-ll-loader]');
    function hideLoader() {
      if (loader) { loader.hidden = true; loader.setAttribute('aria-busy', 'false'); }
      // Reveal elements that should appear together with the content (nav, guest hero).
      var reveal = ROOT.querySelectorAll('[data-ll-reveal]');
      for (var i = 0; i < reveal.length; i++) reveal[i].classList.remove('ll-hidden');
      // Nav is now visible and measurable — set up its scroll-affordance fades.
      try { wireNavScrollHints(); } catch (e) {}
    }
    // Top banner shown when LoyaltyLion never loads (slow/non-responsive API),
    // so the page says it's under maintenance instead of silently going blank.
    function showMaintenance() {
      if (ROOT.querySelector('.lp-maintenance')) return; // idempotent
      var bar = document.createElement('div');
      bar.className = 'lp-maintenance';
      bar.setAttribute('role', 'status');
      bar.innerHTML =
        '<strong>Rewards are temporarily unavailable</strong>' +
        'We’re performing maintenance on our rewards program. Please check back shortly.';
      ROOT.insertBefore(bar, ROOT.firstChild);
    }
    wireRedeemControls(); // static controls — safe before the SDK is ready
    wireExclusiveControls();
    wireScrollSpy();
    wireNavScroll();
    wireFaq();
    ready(12000).then(function (L) {
      // No SDK, or it loaded without the rewards catalogue — treat as an outage.
      if (!L || !L.program || !L.program.rewards) {
        console.warn('[loyalty-lion] SDK not ready; showing maintenance banner');
        showMaintenance();
        hideLoader();
        return;
      }
      var site = L.site || {};
      STATE.customer = L.customer || null;
      STATE.program = L.program || {};
      STATE.sym = site.currency && site.currency.symbol || '$';
      STATE.presentment = presentment();
      STATE.tierId = currentTierId(STATE.customer);
      var customer = STATE.customer, program = STATE.program, sym = STATE.sym, tierId = STATE.tierId;
      function runRenders() {
        try { renderHero(customer); } catch (e) { console.warn('[loyalty-lion] hero', e); }
        try { renderTierStatus(customer, program, sym); } catch (e) { console.warn('[loyalty-lion] tier', e); }
        try { renderActiveRewards(customer); } catch (e) { console.warn('[loyalty-lion] active', e); }
        try { renderEarn(customer, program); } catch (e) { console.warn('[loyalty-lion] earn', e); }
        try { renderTierTable(customer, program); } catch (e) { console.warn('[loyalty-lion] tiers', e); }
        try { renderRedeemMerged(customer, program, tierId); } catch (e) { console.warn('[loyalty-lion] redeem', e); }
        try { renderExclusive(customer, program, tierId); } catch (e) { console.warn('[loyalty-lion] exclusive', e); }
        try { renderHistory(customer); } catch (e) { console.warn('[loyalty-lion] history', e); }
      }
      // Need the cart subtotal before rendering product rewards (minimum-spend gating).
      getCartSubtotal().then(function (sub) { STATE.cartSubtotal = sub; runRenders(); hideLoader(); });

      // Keep the minimum-spend gating in sync when the cart changes in-page
      // (e.g. the cart drawer adds/removes items without a full reload).
      function refreshProductRewards() {
        getCartSubtotal().then(function (sub) {
          STATE.cartSubtotal = sub;
          try { renderRedeemMerged(STATE.customer, STATE.program, STATE.tierId); }
          catch (e) { console.warn('[loyalty-lion] redeem', e); }
          try { renderExclusive(STATE.customer, STATE.program, STATE.tierId); }
          catch (e) { console.warn('[loyalty-lion] exclusive', e); }
        });
      }
      document.addEventListener('cart:update:complete', refreshProductRewards);
      document.addEventListener('cart:add:complete', refreshProductRewards);
    });
  }

  // Single-open accordion for the admin-managed FAQ blocks.
  function wireFaq() {
    var triggers = Array.prototype.slice.call(ROOT.querySelectorAll('.faq-trigger'));
    triggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('.faq-item');
        if (!item) return;
        var isOpen = item.classList.contains('open');
        ROOT.querySelectorAll('.faq-item').forEach(function (i) { i.classList.remove('open'); });
        if (!isOpen) item.classList.add('open');
      });
    });
  }

  // Current fixed-site-header height (announcement bar included).
  // Prefers the live --header-height var theme.js keeps updated, then a
  // direct measurement, then a sane default.
  function headerOffset() {
    var v = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    if (v > 0) return v;
    var hdr = document.querySelector('#shopify-section-header');
    if (hdr && hdr.offsetHeight) return hdr.offsetHeight;
    return 93;
  }

  // Smooth-scroll nav clicks to land each section's heading just below the
  // fixed header, recomputing the offset at click time so it stays accurate
  // regardless of header height changes (mobile, announcement-bar collapse).
  // Toggle edge-fade hints on the horizontally scrollable tab bar so users can
  // tell there are more tabs off-screen. Runs after the nav is revealed.
  function wireNavScrollHints() {
    var wraps = Array.prototype.slice.call(ROOT.querySelectorAll('.nav-scroll'));
    wraps.forEach(function (wrap) {
      var nav = wrap.querySelector('.nav');
      if (!nav) return;
      function update() {
        var max = nav.scrollWidth - nav.clientWidth;
        wrap.classList.toggle('can-left', nav.scrollLeft > 1);
        wrap.classList.toggle('can-right', max > 1 && nav.scrollLeft < max - 1);
      }
      if (!nav._llHintWired) {
        nav.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        // Overlay tap-to-scroll chevron arrows (mobile-only via CSS). They
        // share the .can-left / .can-right state the edge fades already use.
        var mkArrow = function (dir) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'nav-arrow nav-arrow--' + (dir < 0 ? 'prev' : 'next');
          b.setAttribute('aria-label', 'Scroll tabs ' + (dir < 0 ? 'left' : 'right'));
          b.setAttribute('tabindex', '-1');
          b.innerHTML = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="' +
            (dir < 0 ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7') +
            '" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          b.addEventListener('click', function () {
            nav.scrollBy({ left: dir * Math.round(nav.clientWidth * 0.7), behavior: 'smooth' });
          });
          return b;
        };
        wrap.appendChild(mkArrow(-1));
        wrap.appendChild(mkArrow(1));
        nav._llHintWired = true;
      }
      update();
      // Re-measure once fonts/layout settle.
      setTimeout(update, 300);
    });
  }

  function wireNavScroll() {
    var items = Array.prototype.slice.call(ROOT.querySelectorAll('.nav .nav-item'));
    items.forEach(function (a) {
      a.addEventListener('click', function (e) {
        var id = (a.getAttribute('href') || '').replace('#', '');
        if (!id) return;
        var sec = ROOT.querySelector('#' + id);
        if (!sec) return;
        e.preventDefault();
        var top = sec.getBoundingClientRect().top + window.pageYOffset - headerOffset() - 20;
        window.scrollTo({ top: top, behavior: 'smooth' });
        items.forEach(function (n) { n.classList.remove('active'); });
        a.classList.add('active');
        if (history.replaceState) history.replaceState(null, '', '#' + id);
      });
    });
  }

  // Highlight the nav item for the section currently in view.
  function wireScrollSpy() {
    var items = Array.prototype.slice.call(ROOT.querySelectorAll('.nav .nav-item'));
    if (!items.length || !('IntersectionObserver' in window)) return;
    var map = {};
    items.forEach(function (a) {
      var id = (a.getAttribute('href') || '').replace('#', '');
      if (id) map[id] = a;
    });
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        items.forEach(function (a) { a.classList.remove('active'); });
        var a = map[en.target.id];
        if (a) a.classList.add('active');
      });
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 });
    Object.keys(map).forEach(function (id) {
      var sec = ROOT.querySelector('#' + id);
      if (sec) obs.observe(sec);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
